// Page theme defaults, presets, and CSS-variable resolver.
//
// The PageTheme on a PageModel is sparse — only the fields the author has
// changed are persisted. resolveTheme() merges the page's overrides on
// top of the active preset (or "modern-light" if none is selected) and
// produces a fully-populated theme that the canvas/blocks can apply.
//
// The palette is a comprehensive information-hierarchy token set
// (heading, body, muted, link, on-primary, surfaceAlt, semantic states,
// etc.). When a preset or user-supplied palette omits any field, the
// resolver derives it from the explicit ones using HSL-based contrast
// math so every value remains WCAG-AA legible against its intended
// background. This means custom palettes (e.g. AI-generated, or a user
// that only picked a primary + background) still produce a complete,
// readable theme without manual configuration of every token.

import { blockHasItems } from '@twasta/page-schema'
import THEME_PRESET_DATA from './themePresets.json'
import { DEVICE_VISIBILITY_CSS } from './visibility'
import type {
  Background,
  BlockAnimation,
  BlockStyle,
  BlockStyleState,
  BlockTooltip,
  ContentWidthPreset,
  HoverScope,
  Decoration,
  FontFamilyPreset,
  PageLayoutPreset,
  PageTheme,
  PalettePreset,
  ResponsiveStyle,
  Section,
  ShadowPreset,
  ShapeDivider,
  TypographyPreset,
  Viewport,
} from '@twasta/page-schema'

// ─── Font stacks ─────────────────────────────────────────

export const FONT_STACKS: Record<FontFamilyPreset, string> = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  sans: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", Times, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  inter: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  roboto: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  poppins: '"Poppins", "Helvetica Neue", Arial, sans-serif',
  playfair: '"Playfair Display", Georgia, serif',
  merriweather: '"Merriweather", Georgia, serif',
}

// ─── Webfont registry ────────────────────────────────────
// THE single source for "which Google family + weights does this theme need".
// The Google-Fonts family list used to be hardcoded in three places
// (frontend/site-ssr/renderSite.tsx, frontend/index.html,
// runtime/client/index.html) which drifted independently; all three now build
// their <link> from `googleFontsUrl()` below.

/** Preset id → Google Fonts family name (system stacks map to nothing). */
export const GOOGLE_FONT_FAMILY: Partial<Record<FontFamilyPreset, string>> = {
  inter: 'Inter',
  roboto: 'Roboto',
  poppins: 'Poppins',
  playfair: 'Playfair Display',
  merriweather: 'Merriweather',
}

/** Weights requested for a preset family when the theme doesn't say. */
const DEFAULT_FONT_WEIGHTS = [400, 500, 600, 700]

/**
 * Build the Google Fonts CSS2 URL for a set of families, or '' when none of
 * them need a webfont. Families are de-duplicated and sorted so the emitted
 * URL is stable across renders (a churning URL busts the browser cache and
 * makes SSR snapshots noisy).
 */
export function googleFontsUrl(families: (string | undefined)[], weights?: number[]): string {
  const w = (weights && weights.length ? weights : DEFAULT_FONT_WEIGHTS)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b)
  const uniq = [...new Set(families.filter((f): f is string => !!f && !!f.trim()))].sort()
  if (!uniq.length) return ''
  const q = uniq
    .map((f) => `family=${f.trim().replace(/\s+/g, '+')}:wght@${w.join(';')}`)
    .join('&')
  return `https://fonts.googleapis.com/css2?${q}&display=swap`
}

/**
 * Resolve the webfont families a theme actually needs: an explicit
 * `customFontFamily`/`customHeadingFamily` wins over the preset mapping.
 * Returns `{ url, families }` — `url` is '' when the theme is system-only.
 */
export function themeFontLink(theme: ResolvedTheme): { url: string; families: string[] } {
  const t = theme.typography
  const body = t.customFontFamily || GOOGLE_FONT_FAMILY[t.fontFamily]
  const head = t.customHeadingFamily || GOOGLE_FONT_FAMILY[t.headingFamily]
  const families = [...new Set([body, head].filter((f): f is string => !!f))]
  // A system face (Verdana, Helvetica…) is not served by Google Fonts, so it
  // stays in the stack but never in the request.
  const webFamilies = families.filter((f) => !isSystemFontFamily(f))
  return { url: googleFontsUrl(webFamilies, t.customFontWeights), families }
}

/**
 * Faces every desktop OS already ships. A cloned site commonly names one of
 * these (WordPress themes love Verdana/Helvetica); asking Google Fonts for it
 * is a wasted request that returns a 400.
 */
const SYSTEM_FONT_FAMILIES: ReadonlySet<string> = new Set([
  'arial', 'helvetica', 'helvetica neue', 'verdana', 'tahoma', 'trebuchet ms',
  'georgia', 'times', 'times new roman', 'courier', 'courier new', 'segoe ui',
  'system-ui', '-apple-system', 'sans-serif', 'serif', 'monospace', 'impact',
  'lucida grande', 'lucida sans unicode', 'palatino', 'garamond', 'calibri',
])

export function isSystemFontFamily(family: string): boolean {
  return SYSTEM_FONT_FAMILIES.has(family.trim().replace(/^["']|["']$/g, '').toLowerCase())
}

/**
 * Browser-side twin of the SSR `<link>`: make sure the theme's webfonts are
 * loaded on an interactive surface (site viewer, Run view, downloaded
 * runtime). One `<link>` per distinct URL, shared across mounts — a page
 * that switches to a theme whose fonts are already loaded adds nothing.
 * No-op on the server and for system-only themes.
 */
export function ensureThemeFontsLoaded(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  const { url } = themeFontLink(theme)
  if (!url) return
  try {
    const existing = document.head.querySelectorAll('link[data-pg-theme-font]')
    for (const el of Array.from(existing)) {
      if ((el as HTMLLinkElement).href === url) return
    }
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = url
    link.setAttribute('data-pg-theme-font', '')
    document.head.appendChild(link)
  } catch (err) {
    // Fonts are cosmetic — a failure leaves the fallback stack in charge.
    console.warn('[page-runtime] could not load theme fonts', err)
  }
}

/** The CSS font stack for a theme's body / heading family, custom or preset. */
function fontStackFor(preset: FontFamilyPreset, custom?: string): string {
  if (custom && custom.trim()) {
    // Quote the family name and keep the preset stack as the fallback chain,
    // so a webfont that fails to load still lands on a sensible face.
    return `"${custom.trim()}", ${FONT_STACKS[preset]}`
  }
  return FONT_STACKS[preset]
}

// ─── Content-width tokens ────────────────────────────────

// Content-width presets in CSS pixels. Bumped `wide` and `full` upward
// so a typical PC preview actually uses the screen — the previous 1100
// cap meant a desktop preview on a 1440-wide editor area looked nearly
// identical to the 768 tablet preview (both ~800-ish px content). The
// numbers below match the modern marketing-site convention (1280 main,
// 1920 max) and are still safely within the reading-line-length range
// when paired with `paddingX`.
export const CONTENT_WIDTH_PX: Record<ContentWidthPreset, number> = {
  narrow: 680,
  normal: 880,
  wide: 1280,
  full: 1920,
}

// ─── Shadow tokens ───────────────────────────────────────

export const SHADOWS: Record<ShadowPreset, string | undefined> = {
  none: undefined,
  sm: '0 1px 2px rgba(0,0,0,0.06), 0 1px 1px rgba(0,0,0,0.04)',
  md: '0 4px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)',
  lg: '0 10px 20px rgba(0,0,0,0.10), 0 6px 8px rgba(0,0,0,0.06)',
  xl: '0 20px 40px rgba(0,0,0,0.16), 0 10px 16px rgba(0,0,0,0.08)',
}

// ─── Built-in presets ────────────────────────────────────

export interface ThemePreset {
  id: string
  label: string
  description: string
  theme: PageTheme
}

// Curated, professional presets. Each one declares the full hierarchy
// explicitly so headings, body, muted captions, primary buttons, badges,
// callouts, and tables all read with a consistent, WCAG-AA-passing
// contrast against their intended surface. Adding a new preset is the
// recommended way to add a "look"; per-block hardcoded colors should be
// avoided and instead reference the palette tokens via CSS variables.
//
// The data lives in `themePresets.json` — the ONE source. The backend reads
// the same file (`services/page_themes/presets.py`) to score a measured
// design against every built-in look, so a preset edited here is the preset
// the closest-theme matcher compares against. A project's OWN themes
// (Page_Design/themes/) are materialized into each page's `theme` by the
// backend, so nothing here needs to know about them.
export const THEME_PRESETS: ThemePreset[] = THEME_PRESET_DATA as unknown as ThemePreset[]

// New canonical default. Old pages saved with `preset: 'modern' | 'minimal' |
// 'dark'` are mapped to their nearest new preset by `getPreset()` so they
// keep loading.
export const DEFAULT_PRESET_ID = 'modern-light'

// Legacy preset id → current preset id. Lets pages saved before this
// rewrite resolve to a sensible new preset without forcing a migration.
const LEGACY_PRESET_ALIAS: Record<string, string> = {
  modern: 'modern-light',
  minimal: 'minimal-pro',
  dark: 'modern-dark',
}

// Original palettes from the pre-rewrite presets. `smartMergePreset`
// uses these to recognize a saved theme as "untouched legacy preset"
// rather than "user-customized version of the new preset" — without
// this, switching presets on an old page would keep every old color
// because the saved values would all differ from the new preset's
// defaults. Layout and typography are included so a preset switch from
// e.g. legacy 'dark' (gradient bg) to 'modern-light' (white bg)
// actually swaps the background.
const LEGACY_PRESET_THEMES: Record<string, PageTheme> = {
  modern: {
    preset: 'modern',
    palette: {
      primary: '#00bda5', accent: '#0ea5e9', text: '#0f172a',
      textSecondary: '#64748b', background: '#ffffff',
      surface: '#f8fafc', border: '#e2e8f0',
    },
    typography: { fontFamily: 'inter', headingFamily: 'inter', baseSize: 16, scaleRatio: 1.25, lineHeight: 1.6, fontWeight: 400 },
    layout: { contentWidth: 'normal', paddingX: 60, paddingY: 28, background: { kind: 'color', color: '#ffffff' }, align: 'left', blockGap: 14, borderRadius: 8 },
  },
  minimal: {
    preset: 'minimal',
    palette: {
      primary: '#111827', accent: '#374151', text: '#111827',
      textSecondary: '#6b7280', background: '#ffffff',
      surface: '#ffffff', border: '#e5e7eb',
    },
    typography: { fontFamily: 'system', headingFamily: 'system', baseSize: 16, scaleRatio: 1.2, lineHeight: 1.7, fontWeight: 400 },
    layout: { contentWidth: 'narrow', paddingX: 80, paddingY: 48, background: { kind: 'color', color: '#ffffff' }, align: 'left', blockGap: 18, borderRadius: 4 },
  },
  bold: {
    preset: 'bold',
    palette: {
      primary: '#ef4444', accent: '#f59e0b', text: '#0c0a09',
      textSecondary: '#57534e', background: '#fef3c7',
      surface: '#ffffff', border: '#fde68a',
    },
    typography: { fontFamily: 'poppins', headingFamily: 'poppins', baseSize: 17, scaleRatio: 1.333, lineHeight: 1.55, fontWeight: 400 },
    layout: { contentWidth: 'wide', paddingX: 64, paddingY: 32, background: { kind: 'gradient', gradientFrom: '#fef3c7', gradientTo: '#fee2e2', gradientAngle: 135 }, align: 'left', blockGap: 18, borderRadius: 12 },
  },
  editorial: {
    preset: 'editorial',
    palette: {
      primary: '#7c2d12', accent: '#a16207', text: '#1c1917',
      textSecondary: '#78716c', background: '#fafaf9',
      surface: '#ffffff', border: '#e7e5e4',
    },
    typography: { fontFamily: 'merriweather', headingFamily: 'playfair', baseSize: 17, scaleRatio: 1.333, lineHeight: 1.75, fontWeight: 400 },
    layout: { contentWidth: 'narrow', paddingX: 72, paddingY: 56, background: { kind: 'color', color: '#fafaf9' }, align: 'left', blockGap: 18, borderRadius: 2 },
  },
  dark: {
    preset: 'dark',
    palette: {
      primary: '#22d3ee', accent: '#a78bfa', text: '#e2e8f0',
      textSecondary: '#94a3b8', background: '#0f172a',
      surface: '#1e293b', border: '#334155',
    },
    typography: { fontFamily: 'inter', headingFamily: 'inter', baseSize: 16, scaleRatio: 1.25, lineHeight: 1.6, fontWeight: 400 },
    layout: { contentWidth: 'normal', paddingX: 60, paddingY: 28, background: { kind: 'gradient', gradientFrom: '#0f172a', gradientTo: '#1e293b', gradientAngle: 180 }, align: 'left', blockGap: 14, borderRadius: 8 },
  },
}

export function getPreset(id?: string): ThemePreset {
  if (!id) return THEME_PRESETS[0]
  const aliased = LEGACY_PRESET_ALIAS[id] || id
  return THEME_PRESETS.find((p) => p.id === aliased) || THEME_PRESETS[0]
}

// ─── Resolve a sparse theme to a fully-populated one ────────

export interface ResolvedTheme {
  // The resolved palette is fully populated — every optional field on
  // PalettePreset is filled in either from the user's override, the
  // active preset, or the auto-derivation step in `resolveTheme()`.
  palette: Required<PalettePreset>
  typography: Required<TypographyPreset>
  layout: Required<Omit<PageLayoutPreset, 'maxWidth'>> & { maxWidth?: number }
}

export function mergeTheme(base: PageTheme, override?: PageTheme): PageTheme {
  return {
    preset: override?.preset || base.preset,
    palette: { ...(base.palette || {}), ...(override?.palette || {}) },
    typography: { ...(base.typography || {}), ...(override?.typography || {}) },
    layout: { ...(base.layout || {}), ...(override?.layout || {}) },
  }
}

export function resolveTheme(theme?: PageTheme): ResolvedTheme {
  const preset = getPreset(theme?.preset).theme
  const merged = mergeTheme(preset, theme)
  const palette = derivePalette(merged.palette || {})
  const typography = merged.typography || {}
  const layout = merged.layout || {}
  return {
    palette,
    typography: {
      fontFamily: typography.fontFamily || 'inter',
      headingFamily: typography.headingFamily || typography.fontFamily || 'inter',
      baseSize: typography.baseSize || 16,
      scaleRatio: typography.scaleRatio || 1.25,
      lineHeight: typography.lineHeight || 1.6,
      fontWeight: typography.fontWeight || 400,
      fluid: typography.fluid ?? false,
      headingWeight: typography.headingWeight ?? 700,
      headingLineHeight: typography.headingLineHeight ?? 1.2,
      headingLetterSpacing: typography.headingLetterSpacing ?? 0,
      customFontFamily: typography.customFontFamily ?? '',
      customHeadingFamily: typography.customHeadingFamily ?? '',
      customFontWeights: typography.customFontWeights ?? [],
      eyebrowSize: typography.eyebrowSize ?? 12,
      eyebrowWeight: typography.eyebrowWeight ?? 700,
      eyebrowTracking: typography.eyebrowTracking ?? 0.08,
      eyebrowTransform: typography.eyebrowTransform ?? 'uppercase',
      bodyMaxWidth: typography.bodyMaxWidth ?? 0,
    },
    layout: {
      contentWidth: layout.contentWidth || 'normal',
      paddingX: layout.paddingX ?? 60,
      paddingY: layout.paddingY ?? 28,
      background: layout.background || { kind: 'none' },
      align: layout.align || 'left',
      blockGap: layout.blockGap ?? 14,
      borderRadius: layout.borderRadius ?? 8,
      buttonRadius: layout.buttonRadius ?? 8,
      maxWidth: layout.maxWidth,
    },
  }
}

// ─── Palette derivation: fill in every missing token ─────
// Strategy:
//   1) Honor explicit values first (including the legacy `textSecondary`
//      alias for `muted`).
//   2) Fill in the canonical typographic / surface / brand tokens with
//      sensible derived defaults from the explicit ones.
//   3) Compute `on*` / `inverted` from luminance so text on any picked
//      brand color or background remains readable.
// Why: the user (or AI) often only sets a primary + background. The
// derivation makes sure every block has a contrast-safe color to read
// without the user having to pick all 30 tokens.
function derivePalette(p: PalettePreset): Required<PalettePreset> {
  // Resolve the canonical "is the page dark or light?" question first so
  // every downstream derivation can pick the right direction.
  const background = p.background || '#ffffff'
  const isDark = isColorDark(background)

  // Body text. Prefer explicit `text`, but fall back to a contrast-safe
  // value when the explicit value fails WCAG AA against the background.
  // This rescues saved pages where (e.g.) a previous dark theme persisted
  // `text: white` and the user later changed only the background to a
  // light color — without the safety override, the body text would be
  // invisible until the user manually re-picked it.
  const text = ensureContrast(p.text, background, 4.5, isDark ? '#e6edf3' : '#1e293b')
  // Heading is allowed slightly higher contrast (it's bigger/bolder).
  const heading = ensureContrast(p.heading, background, 4.5, isDark ? '#f8fafc' : '#0f172a')
  // `muted` accepts the legacy `textSecondary` alias. Allow 3:1 since
  // captions are smaller; still must remain readable against background.
  const mutedExplicit = p.muted || p.textSecondary
  const muted = ensureContrast(mutedExplicit, background, 3.0, isDark ? '#94a3b8' : '#475569')
  const subtle = ensureContrast(p.subtle, background, 2.5, isDark ? '#64748b' : '#94a3b8')

  // Brand colors. Primary defaults to a teal that reads on either side.
  const primary = p.primary || (isDark ? '#22d3ee' : '#0ea5a4')
  const primaryHover = p.primaryHover || shade(primary, isDark ? 0.15 : -0.10)
  // `on*` colors must contrast their own brand color, not the page bg.
  // Override explicit on-color too if it fails (e.g. saved value of
  // white-on-yellow-primary).
  const onPrimary = ensureContrast(p.onPrimary, primary, 4.5, pickOn(primary))

  const accent = p.accent || (isDark ? '#a78bfa' : '#0284c7')
  const accentHover = p.accentHover || shade(accent, isDark ? 0.15 : -0.10)
  const onAccent = ensureContrast(p.onAccent, accent, 4.5, pickOn(accent))

  // Links: default to primary on light, lighter primary on dark. Must
  // contrast the page background (links sit on body background).
  const link = ensureContrast(p.link, background, 4.5, isDark ? primaryHover : primary)
  const linkHover = p.linkHover || shade(link, isDark ? 0.15 : -0.15)

  // Inverted text: used on a brand-colored band (hero, CTA banner). It
  // should contrast a *colored* surface, not the page bg, so the
  // safety net is just luminance-flip from the background.
  const inverted = p.inverted || (isDark ? '#0f172a' : '#ffffff')

  // Surface stack. Each level steps slightly toward (light) or away
  // from (dark) the background so cards/tables visually separate.
  const surface = p.surface || (isDark ? lighten(background, 0.04) : '#f8fafc')
  const surfaceAlt = p.surfaceAlt || (isDark ? lighten(background, 0.07) : darken(background, 0.03))
  const surfaceMuted = p.surfaceMuted || (isDark ? lighten(background, 0.10) : darken(background, 0.06))
  const elevated = p.elevated || (isDark ? lighten(background, 0.08) : '#ffffff')

  const border = p.border || (isDark ? lighten(background, 0.12) : darken(background, 0.10))
  const borderStrong = p.borderStrong || (isDark ? lighten(background, 0.20) : darken(background, 0.18))

  // Surface-specific text colors. When the surface differs in luminance
  // from the background (e.g. white card on dark page), the body text
  // tuned for the page may not contrast the surface. Compute fresh and
  // ensure contrast against each surface explicitly.
  const onSurface = ensureContrast(p.onSurface, surface, 4.5, isColorDark(surface) ? '#f8fafc' : '#1e293b')
  const onSurfaceAlt = ensureContrast(p.onSurfaceAlt, surfaceAlt, 4.5, isColorDark(surfaceAlt) ? '#f8fafc' : '#1e293b')

  // Semantic states. Defaults are tuned for both light and dark themes
  // by mixing tinted-bg with a saturated foreground.
  const info = p.info || (isDark ? '#38bdf8' : '#0284c7')
  const infoBg = p.infoBg || (isDark ? withAlpha(info, 0.14) : '#e0f2fe')
  const success = p.success || (isDark ? '#34d399' : '#059669')
  const successBg = p.successBg || (isDark ? withAlpha(success, 0.14) : '#d1fae5')
  const warning = p.warning || (isDark ? '#fbbf24' : '#d97706')
  const warningBg = p.warningBg || (isDark ? withAlpha(warning, 0.14) : '#fef3c7')
  const error = p.error || (isDark ? '#f87171' : '#dc2626')
  const errorBg = p.errorBg || (isDark ? withAlpha(error, 0.14) : '#fee2e2')

  return {
    primary, primaryHover, onPrimary,
    accent, accentHover, onAccent,
    heading, text, muted, subtle, link, linkHover, inverted,
    onSurface, onSurfaceAlt, textSecondary: muted, // back-compat alias
    background, surface, surfaceAlt, surfaceMuted, elevated, border, borderStrong,
    info, infoBg, success, successBg, warning, warningBg, error, errorBg,
  }
}

// ─── Background → CSS background-* declarations ────────

// Build the `linear-gradient(…)` / `radial-gradient(…)` value for a gradient
// background. A `stops` array of 2+ entries replaces the two-stop
// gradientFrom/gradientTo shorthand; `gradientType: 'radial'` swaps the
// function and uses `gradientShape` as the center. Shared by the static and
// the animated gradient kinds.
export function gradientValue(bg: Background): string {
  const stops = Array.isArray(bg.stops) && bg.stops.length >= 2
    ? bg.stops.map((s) => (typeof s.at === 'number' ? `${s.color} ${s.at}%` : s.color))
    : [bg.gradientFrom || '#000', bg.gradientTo || '#000']
  if (bg.gradientType === 'radial') {
    const at = RADIAL_POSITION[bg.gradientShape || 'center'] || 'center'
    return `radial-gradient(circle at ${at}, ${stops.join(', ')})`
  }
  const angle = bg.gradientAngle ?? 135
  return `linear-gradient(${angle}deg, ${stops.join(', ')})`
}

const RADIAL_POSITION: Record<string, string> = {
  center: 'center', top: 'top', bottom: 'bottom', left: 'left', right: 'right',
  'top-left': 'top left', 'top-right': 'top right',
  'bottom-left': 'bottom left', 'bottom-right': 'bottom right',
}

/**
 * Slow-drifting gradient. The element paints an oversized gradient and
 * animates its `background-position`, which is compositor-cheap and needs no
 * pseudo-elements. Inside the reduced-motion guard — a visitor who asked for
 * less motion gets the same gradient, just still.
 */
export const ANIMATED_BG_CSS = `
.pg-bg-drift{background-size:220% 220%;background-position:0% 50%}
/* A band with a <video> backdrop: the video is absolutely positioned at
   z-index 0, and an absolutely-positioned element paints ABOVE in-flow
   siblings. Lifting every non-video child into its own stacking position is
   what keeps the copy on top of the footage — done here once rather than as
   an inline z-index on each band that supports a background. */
.pg-has-video{position:relative;overflow:hidden}
.pg-has-video > *:not(video){position:relative;z-index:1}
@media (prefers-reduced-motion: no-preference){
  @keyframes pg-bg-drift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  .pg-bg-drift{animation:pg-bg-drift var(--pg-bg-drift-speed,18s) ease-in-out infinite}
}
`

/** True when the background is a muted looping video (needs a <video> layer). */
export function hasVideoBackground(bg: Background | undefined): boolean {
  return !!(bg && bg.kind === 'video' && bg.videoUrl)
}

export function backgroundCss(bg: Background | undefined): React.CSSProperties {
  if (!bg || bg.kind === 'none') return {}
  // Explicit transparent overrides any base surface fill (unlike `none`,
  // which emits nothing and leaves the element's own background in place).
  if (bg.kind === 'transparent') return { background: 'transparent' }
  if (bg.kind === 'color') return { background: bg.color || 'transparent' }
  if (bg.kind === 'gradient' || bg.kind === 'gradient_animated') {
    return { backgroundImage: gradientValue(bg) }
  }
  if (bg.kind === 'video') {
    // The poster paints the element itself, so the block is never blank —
    // before the video buffers, on the static SSR site (no <video> autoplay
    // guarantee), and under reduced motion where the video layer is skipped.
    if (!bg.posterUrl) return { background: bg.color || 'var(--pg-surface)' }
    return {
      backgroundImage: bgImageValue({ ...bg, imageUrl: bg.posterUrl }),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }
  if (bg.kind === 'image') {
    // Parallax uses `background-attachment: fixed` (see parallaxBgCss) — the
    // image still paints on the element (never blank), and pins to the viewport
    // while content scrolls over it.
    if (bg.parallax) return parallaxBgCss(bg)
    return {
      backgroundImage: bgImageValue(bg),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }
  return {}
}

// True when the background paints an opaque-ish surface (color / gradient /
// image) — i.e. NOT undefined / 'none' / 'transparent'. Contrast and
// decorative heuristics (white text on a band, default padding/radius around a
// filled block) key off this so an explicit `transparent` is treated like
// `none` instead of being mistaken for a dark fill.
export function bgHasFill(bg: Background | undefined): boolean {
  return !!bg && bg.kind !== 'none' && bg.kind !== 'transparent'
}

// Layered background-image value: an optional rgba overlay drawn over the image.
function bgImageValue(bg: Background): string {
  const overlay = bg.overlay ? `linear-gradient(${bg.overlay}, ${bg.overlay}), ` : ''
  return `${overlay}url(${JSON.stringify(bg.imageUrl || '')})`
}

// ─── Parallax ─────────────────────────────────────────────
// A background image with parallax stays pinned to the viewport while the page
// scrolls over it, via the classic `background-attachment: fixed`. Chosen for
// reliability: it paints the image directly on the element (so it can never go
// blank), needs no global stylesheet or scroll-driven-animation support, and
// works across all desktop browsers including Safari/Firefox. Mobile browsers
// ignore `fixed` and degrade to a static cover image. The same treatment is
// used for section/block backgrounds (parallaxBgCss, via backgroundCss /
// backgroundStyle) and for the page-wide background (pageParallaxStyle).
export const PARALLAX_CLASS = 'pg-parallax'

// Retained as an empty string for backward-compat with the per-surface
// injection sites; the fixed-attachment technique needs no global CSS.
export const PARALLAX_CSS = ''

// True when a background should parallax (image kind, flag set, has a url).
export function hasParallax(bg: Background | undefined): boolean {
  return !!(bg && bg.kind === 'image' && bg.parallax && bg.imageUrl)
}

// The fixed-attachment background declarations for a parallax image.
export function parallaxBgCss(bg: Background): React.CSSProperties {
  return {
    backgroundImage: bgImageValue(bg),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  }
}

// Page-wide parallax background — same fixed-attachment treatment, applied on
// the page shell. Returns {} when not parallax.
export function pageParallaxStyle(bg: Background | undefined): React.CSSProperties {
  return hasParallax(bg) ? parallaxBgCss(bg!) : {}
}

// ════════════════════════════════════════════════════════════════════
// Motion — block entrance animations
// --------------------------------------------------------------------
// Three deliberate constraints, all of them load-bearing:
//
//  1. CSS keyframes + `transition` only. NOT `animation-timeline: view()` —
//     support is uneven and this codebase already retreated from it once
//     (that is why the parallax implementation is `background-attachment`).
//  2. ONE shared IntersectionObserver per page for the scroll/enter
//     triggers, following the same "tiny inline vanilla script" precedent as
//     the hero carousel, the countdown ticker and the auto-hide header — so
//     it works on the STATIC published site, where a React hook would
//     silently do nothing.
//  3. Degrade to VISIBLE. The animated element's base state is its FINAL
//     state; the keyframes run from a hidden start toward it. A blocked
//     script, an old browser, or `prefers-reduced-motion: reduce` therefore
//     yields a fully readable page rather than a blank one. Never invert
//     this by giving the base rule `opacity: 0`.
// ════════════════════════════════════════════════════════════════════

/** Class stamped on a block that carries an animation. */
export const ANIM_CLASS = 'pg-anim'
/** Attribute the reveal script observes. */
export const REVEAL_ATTR = 'data-pg-reveal'
/** Class the reveal script adds once the element has entered the viewport. */
export const REVEAL_ON_CLASS = 'pg-in'

/**
 * The keyframes + base rules for every animation preset.
 *
 * Injected ONCE per page (by ThemedPageShell on the React surfaces, inside
 * the document <style> on SSR). Everything lives inside
 * `@media (prefers-reduced-motion: no-preference)`, so a visitor who asked
 * for less motion gets the static page with no further branching.
 */
export const ANIMATION_CSS = `
@media (prefers-reduced-motion: no-preference){
  @keyframes pg-a-fade{from{opacity:0}to{opacity:1}}
  @keyframes pg-a-fade-up{from{opacity:0;transform:translate3d(0,var(--pg-anim-shift,24px),0)}to{opacity:1;transform:none}}
  @keyframes pg-a-fade-down{from{opacity:0;transform:translate3d(0,calc(-1 * var(--pg-anim-shift,24px)),0)}to{opacity:1;transform:none}}
  @keyframes pg-a-fade-left{from{opacity:0;transform:translate3d(var(--pg-anim-shift,24px),0,0)}to{opacity:1;transform:none}}
  @keyframes pg-a-fade-right{from{opacity:0;transform:translate3d(calc(-1 * var(--pg-anim-shift,24px)),0,0)}to{opacity:1;transform:none}}
  @keyframes pg-a-scale-in{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:none}}
  @keyframes pg-a-slide-up{from{transform:translate3d(0,var(--pg-anim-shift,40px),0)}to{transform:none}}
  @keyframes pg-a-slide-down{from{transform:translate3d(0,calc(-1 * var(--pg-anim-shift,40px)),0)}to{transform:none}}
  @keyframes pg-a-zoom-in{from{opacity:0;transform:scale(.8)}to{opacity:1;transform:none}}
  @keyframes pg-a-blur-in{from{opacity:0;filter:blur(12px)}to{opacity:1;filter:blur(0)}}
  @keyframes pg-a-clip-wipe{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0 0 0)}}

  /* Scroll/enter-triggered blocks start hidden ONLY once the observer has
     confirmed it is alive (it stamps [data-pg-reveal-armed] on <html> in its
     first statement). If the script never runs, this rule never matches and
     the block simply renders — the degrade-to-visible contract. */
  html[data-pg-reveal-armed] [${REVEAL_ATTR}]:not(.${REVEAL_ON_CLASS}){
    opacity:0;
  }
  html[data-pg-reveal-armed] [${REVEAL_ATTR}][data-pg-preset="slide-up"]:not(.${REVEAL_ON_CLASS}),
  html[data-pg-reveal-armed] [${REVEAL_ATTR}][data-pg-preset="slide-down"]:not(.${REVEAL_ON_CLASS}),
  html[data-pg-reveal-armed] [${REVEAL_ATTR}][data-pg-preset="clip-wipe"]:not(.${REVEAL_ON_CLASS}){
    opacity:1;
  }

  /* The animation itself runs when the trigger fires: immediately for
     'load', on .pg-in for scroll/enter, on :hover for 'hover'. */
  .${ANIM_CLASS}[data-pg-trigger="load"],
  .${ANIM_CLASS}.${REVEAL_ON_CLASS}{
    animation-name:var(--pg-anim-name);
    animation-duration:var(--pg-anim-duration,600ms);
    animation-timing-function:var(--pg-anim-easing,ease-out);
    animation-delay:var(--pg-anim-delay,0ms);
    animation-fill-mode:both;
  }
  .${ANIM_CLASS}[data-pg-trigger="hover"]:hover,
  .${ANIM_CLASS}[data-pg-trigger="hover"]:focus-within{
    animation-name:var(--pg-anim-name);
    animation-duration:var(--pg-anim-duration,600ms);
    animation-timing-function:var(--pg-anim-easing,ease-out);
    animation-fill-mode:both;
  }

  /* Stagger: each direct child of a staggered block starts one step later.
     Capped at 24 children — beyond that the tail would feel broken rather
     than choreographed, so the rest share the last step. */
  .pg-stagger.${REVEAL_ON_CLASS} > *{
    animation-name:var(--pg-anim-name);
    animation-duration:var(--pg-anim-duration,600ms);
    animation-timing-function:var(--pg-anim-easing,ease-out);
    animation-fill-mode:both;
    animation-delay:calc(var(--pg-anim-delay,0ms) + var(--pg-stagger,80ms) * var(--pg-i,0));
  }
  html[data-pg-reveal-armed] .pg-stagger[${REVEAL_ATTR}]:not(.${REVEAL_ON_CLASS}) > *{opacity:0}
  /* The parent of a staggered group must not animate itself — its children do. */
  .pg-stagger.${REVEAL_ON_CLASS}{animation:none!important;opacity:1!important}
${staggerIndexRules(24)}
}

/* The canvas replay control removes and re-adds .pg-in; this forces the
   browser to restart the animation rather than treat it as unchanged. */
.pg-anim-replay{animation:none!important}
`

/** `.pg-stagger > *:nth-child(n){--pg-i:n-1}` for the first `n` children. */
function staggerIndexRules(n: number): string {
  let out = ''
  for (let i = 1; i <= n; i++) out += `  .pg-stagger > *:nth-child(${i}){--pg-i:${i - 1}}\n`
  return out
}

/** Keyframe name for a preset ('none' has none). */
function animationName(preset: BlockAnimation['preset']): string | null {
  if (!preset || preset === 'none') return null
  return `pg-a-${preset}`
}

/** True when the block carries a real (non-'none') animation. */
export function hasAnimation(anim: BlockAnimation | undefined): boolean {
  return !!(anim && anim.preset && anim.preset !== 'none')
}

/**
 * True when the animation needs the IntersectionObserver (i.e. it fires when
 * the element enters the viewport rather than on load / on hover).
 */
export function animationNeedsObserver(anim: BlockAnimation | undefined): boolean {
  if (!hasAnimation(anim)) return false
  const on = anim!.on || 'enter'
  return on === 'enter' || on === 'scroll'
}

/** The class list an animated wrapper carries. */
export function animationClasses(anim: BlockAnimation | undefined): string[] {
  if (!hasAnimation(anim)) return []
  const out = [ANIM_CLASS]
  if (anim!.stagger && anim!.stagger > 0) out.push('pg-stagger')
  return out
}

/**
 * The DOM attributes an animated wrapper carries: the trigger, the preset
 * (for the base-state exceptions above), the reveal hook, and `once`.
 * Returned as a plain prop bag so React surfaces spread it and the SSR
 * emitter serialises the same names.
 */
export function animationAttrs(anim: BlockAnimation | undefined): Record<string, string> {
  if (!hasAnimation(anim)) return {}
  const on = anim!.on || 'enter'
  const attrs: Record<string, string> = {
    'data-pg-trigger': on,
    'data-pg-preset': anim!.preset,
  }
  if (animationNeedsObserver(anim)) {
    attrs[REVEAL_ATTR] = ''
    // 'scroll' replays each time the block re-enters unless the author
    // explicitly asked for once; 'enter' defaults to playing once.
    const once = anim!.once ?? (on === 'enter')
    if (!once) attrs['data-pg-repeat'] = ''
  }
  return attrs
}

/**
 * The per-block animation custom properties. These are plain inline styles
 * (no media query needed — they only take effect through the ANIMATION_CSS
 * rules, which are themselves inside the reduced-motion guard).
 */
export function animationVars(anim: BlockAnimation | undefined): React.CSSProperties {
  if (!hasAnimation(anim)) return {}
  const name = animationName(anim!.preset)
  if (!name) return {}
  const out: Record<string, string> = {
    '--pg-anim-name': name,
    '--pg-anim-duration': `${clampMs(anim!.duration, 600, 50, 4000)}ms`,
    '--pg-anim-easing': anim!.easing || 'ease-out',
  }
  if (anim!.delay) out['--pg-anim-delay'] = `${clampMs(anim!.delay, 0, 0, 10000)}ms`
  if (anim!.stagger && anim!.stagger > 0) {
    out['--pg-stagger'] = `${clampMs(anim!.stagger, 80, 10, 1000)}ms`
  }
  return out as React.CSSProperties
}

function clampMs(v: number | undefined, dflt: number, min: number, max: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return dflt
  return Math.max(min, Math.min(max, Math.round(v)))
}

/**
 * The shared reveal script — the one piece of JS the motion layer needs.
 *
 * Emitted once per page: interactive React surfaces run it from
 * ThemedPageShell, the static SSR site inlines it in a <script> tag exactly
 * like the hero-carousel and countdown scripts. It is deliberately tiny,
 * dependency-free, and safe to run twice.
 *
 * It arms itself by stamping `data-pg-reveal-armed` on <html> BEFORE
 * observing anything — that attribute is what activates the hidden base
 * state in ANIMATION_CSS, so if this script is blocked or errors the blocks
 * were never hidden in the first place.
 */
export function revealScript(): string {
  return `(function(){
  if(window.__pgReveal)return;
  var mq=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)');
  if(mq&&mq.matches)return;              /* honour reduced motion: no hiding, no observing */
  if(!('IntersectionObserver' in window))return;   /* no observer → leave everything visible */
  window.__pgReveal=1;
  var root=document.documentElement;
  root.setAttribute('data-pg-reveal-armed','');
  var io=new IntersectionObserver(function(entries){
    for(var i=0;i<entries.length;i++){
      var e=entries[i],el=e.target;
      if(e.isIntersecting){
        el.classList.add('${REVEAL_ON_CLASS}');
        if(!el.hasAttribute('data-pg-repeat'))io.unobserve(el);
      }else if(el.hasAttribute('data-pg-repeat')){
        el.classList.remove('${REVEAL_ON_CLASS}');
      }
    }
  },{rootMargin:'0px 0px -10% 0px',threshold:0.05});
  function scan(){
    var els=document.querySelectorAll('[${REVEAL_ATTR}]:not([data-pg-observed])');
    for(var i=0;i<els.length;i++){els[i].setAttribute('data-pg-observed','');io.observe(els[i]);}
  }
  scan();
  /* Blocks can arrive later (data-bound repeats, a drawer opening, an SPA
     route change), so keep watching the tree for new reveal targets. */
  if(window.MutationObserver){
    new MutationObserver(scan).observe(document.body,{childList:true,subtree:true});
  }
  window.__pgRevealScan=scan;
})();`
}

// ════════════════════════════════════════════════════════════════════
// Parallax (retained API)
// ════════════════════════════════════════════════════════════════════
// `parallaxClass` / `parallaxVars` were no-ops kept only so ~15 call sites
// kept compiling after the switch to `background-attachment: fixed`. They
// are gone; `parallaxBgCss` (used by `backgroundCss`) is the whole feature.

// Background for a site header/footer CHROME bar. The bar's intended color is
// `palette.background` (a theme token the agent sets). The page-level
// `layout.background` defaults to a solid white and would otherwise CLOBBER a
// dark header (two `background:` declarations on one element → the later one
// wins, leaving an invisible header). So only honor `layout.background` when
// it's a gradient or image — something the palette token can't express;
// otherwise fall back to the palette background.
export function chromeBackgroundCss(theme: ResolvedTheme): React.CSSProperties {
  const bg = theme.layout.background
  if (bg && (bg.kind === 'gradient' || bg.kind === 'image')) return backgroundCss(bg)
  return { background: theme.palette.background }
}

// ─── Theme → CSS variables on the canvas wrapper ───────
// Single canonical namespace: `--pg-*`. Renderers in the page designer
// and the Angular runtime both read these directly. Section overrides
// (resolved from page.designTokens.colorSchemes) write the same keys
// one DOM level deeper inside SectionStrip.
// A heading size: a fixed px string, or — when `fluid` — a clamp() that scales
// the heading between ~0.8× (phones) and 1× (desktop) of the computed size.
function headingSize(px: number, fluid: boolean): string {
  if (!fluid) return `${px.toFixed(2)}px`
  const min = px * 0.8
  const intercept = px * 0.55
  return `clamp(${min.toFixed(2)}px, ${intercept.toFixed(2)}px + 1.1vw, ${px.toFixed(2)}px)`
}

// The canonical heading style — size from the theme scale (`--pg-h1/2/3` by
// level), plus the heading weight / line-height / tracking / family tokens.
// EVERY heading renderer (design canvas, in-app preview/run, deployed runtime,
// SSR) spreads this so heading typography is theme-driven and identical across
// surfaces (it was previously hardcoded per surface and drifted).
/**
 * A font size that scales with the THEME's base size, as `rem` was meant to.
 *
 * `rem` is relative to <html>, which a page does not own (the platform's
 * own stylesheet sets it in the in-app viewer, the browser default on a
 * published site), so every `0.88rem` in a block ignored the theme's
 * `baseSize` — raising it to 18 changed nothing but paragraphs. This is the
 * same ratio against `--pg-base-size`, which every themed surface emits: at
 * the default 16px it is pixel-identical to the rem it replaces.
 */
/**
 * The page's side gutter as CSS. The theme's `paddingX` is a DESKTOP gutter:
 * kept exactly from the tablet breakpoint up, and scaled with the viewport
 * below it (never under 16px) — a fixed 60px left a 390px phone 270px of
 * content, so headers stacked and every band looked squeezed. Every surface
 * that pads a page or its header/footer uses this, so they shrink together.
 */
export function pagePaddingXCss(px: number): string {
  if (!Number.isFinite(px) || px <= 16) return `${Math.max(0, px || 0)}px`
  const vw = (px / RESPONSIVE_BREAKPOINTS.tablet) * 100
  return `min(${px}px, max(16px, ${vw.toFixed(3)}vw))`
}

export function themeRem(n: number): string {
  return `calc(var(--pg-base-size, 16px) * ${n})`
}

export function headingFontVars(level: 1 | 2 | 3): React.CSSProperties {
  return {
    // A heading block's own size (`--pg-text-size`, set on its wrapper by
    // blockStyleCss) wins over the theme scale.
    fontSize: `var(--pg-text-size, var(--pg-h${level}))`,
    fontWeight: 'var(--pg-heading-weight, 700)' as unknown as number,
    lineHeight: 'var(--pg-heading-line-height, 1.2)' as unknown as number,
    letterSpacing: 'var(--pg-heading-tracking, normal)',
    fontFamily: 'var(--pg-heading-font, var(--pg-font))',
  }
}

export function themeCssVars(theme: ResolvedTheme): React.CSSProperties {
  const t = theme.typography
  const base = t.baseSize
  const ratio = t.scaleRatio
  const p = theme.palette
  return {
    // ── Brand ──
    ['--pg-primary' as never]: p.primary,
    ['--pg-primary-hover' as never]: p.primaryHover,
    ['--pg-on-primary' as never]: p.onPrimary,
    ['--pg-accent' as never]: p.accent,
    ['--pg-accent-hover' as never]: p.accentHover,
    ['--pg-on-accent' as never]: p.onAccent,
    // ── Text hierarchy ──
    ['--pg-heading' as never]: p.heading,
    ['--pg-text' as never]: p.text,
    ['--pg-muted' as never]: p.muted,
    ['--pg-subtle' as never]: p.subtle,
    ['--pg-link' as never]: p.link,
    ['--pg-link-hover' as never]: p.linkHover,
    ['--pg-inverted' as never]: p.inverted,
    ['--pg-on-surface' as never]: p.onSurface,
    ['--pg-on-surface-alt' as never]: p.onSurfaceAlt,
    // Back-compat alias — older code paths still read --pg-text-secondary.
    ['--pg-text-secondary' as never]: p.muted,
    // ── Surfaces ──
    ['--pg-bg' as never]: p.background,
    // Alias: the standalone runtime client reads `--pg-background`. Emit both
    // names so every surface resolves the page background without a literal
    // fallback (see ThemedPageShell + the P0 theme-parity work).
    ['--pg-background' as never]: p.background,
    ['--pg-surface' as never]: p.surface,
    ['--pg-surface-alt' as never]: p.surfaceAlt,
    ['--pg-surface-muted' as never]: p.surfaceMuted,
    ['--pg-elevated' as never]: p.elevated,
    ['--pg-border' as never]: p.border,
    ['--pg-border-strong' as never]: p.borderStrong,
    // ── Semantic states ──
    ['--pg-info' as never]: p.info,
    ['--pg-info-bg' as never]: p.infoBg,
    ['--pg-success' as never]: p.success,
    ['--pg-success-bg' as never]: p.successBg,
    ['--pg-warning' as never]: p.warning,
    ['--pg-warning-bg' as never]: p.warningBg,
    ['--pg-error' as never]: p.error,
    ['--pg-error-bg' as never]: p.errorBg,
    // ── Typography (h3 → h2 → h1 climb the ratio) ──
    // A `customFontFamily` (any Google family) wins over the preset stack;
    // the preset stays as the fallback chain so a failed webfont still
    // lands somewhere sensible.
    ['--pg-font' as never]: fontStackFor(t.fontFamily, t.customFontFamily),
    ['--pg-heading-font' as never]: fontStackFor(t.headingFamily, t.customHeadingFamily),
    ['--pg-base-size' as never]: `${base}px`,
    // …and APPLY it. The variable was emitted and read by nothing, so body
    // text inherited whatever the HOST page set (the platform's own 14px in
    // the in-app viewer) and the theme's base size changed nothing. Every
    // surface spreads this object on its page root — the designer canvas,
    // ThemedPageShell, the in-app site viewer's main + chrome, the static
    // SSR page + chrome, pop-ups — so one line reaches all of them. At the
    // default 16px it is what a browser already does.
    fontSize: 'var(--pg-base-size, 16px)',
    // The FONT had the same gap: `--pg-font` was emitted but only
    // ThemedPageShell applied it, so the in-app site viewer showed every
    // page — a Poppins theme included — in the platform's own IBM Plex Sans.
    fontFamily: 'var(--pg-font)',
    ['--pg-line-height' as never]: String(t.lineHeight),
    ['--pg-font-weight' as never]: String(t.fontWeight),
    // Heading scale (h3=¹, h2=², h1=³ up the ratio). Emitted as either a fixed
    // px size or, when `fluid`, a clamp() that scales the heading with the
    // viewport (smaller on phones, capped at the computed size on desktop).
    // Every heading renderer reads these so the theme controls heading sizes.
    ['--pg-h3' as never]: headingSize(base * ratio, t.fluid),
    ['--pg-h2' as never]: headingSize(base * ratio * ratio, t.fluid),
    ['--pg-h1' as never]: headingSize(base * ratio * ratio * ratio, t.fluid),
    ['--pg-heading-weight' as never]: String(t.headingWeight),
    ['--pg-heading-line-height' as never]: String(t.headingLineHeight),
    ['--pg-heading-tracking' as never]: t.headingLetterSpacing ? `${t.headingLetterSpacing}em` : 'normal',
    // ── Layout ──
    // Single source of truth for the page layout contract. Every surface
    // (canvas, in-app preview, public SSR, standalone runtime) reads these
    // instead of re-deriving width/padding inline. `--pg-content-width` and
    // `--pg-content-max` are aliases — SSR historically read the former, the
    // standalone client the latter; emit both so neither falls back to a
    // frozen literal.
    ['--pg-content-width' as never]: `${contentWidthPx(theme.layout)}px`,
    ['--pg-content-max' as never]: `${contentWidthPx(theme.layout)}px`,
    ['--pg-page-padding-x' as never]: pagePaddingXCss(theme.layout.paddingX),
    ['--pg-page-padding-y' as never]: `${theme.layout.paddingY}px`,
    ['--pg-align' as never]: theme.layout.align,
    ['--pg-radius' as never]: `${theme.layout.borderRadius}px`,
    ['--pg-button-radius' as never]: `${theme.layout.buttonRadius ?? 8}px`,
    ['--pg-block-gap' as never]: `${theme.layout.blockGap}px`,
    // ── Eyebrow / overline token ──
    // The small-caps label above a hero or section heading. Every block used
    // to hardcode its own size/weight/tracking; they all read these now, so
    // the look is consistent and themeable in one place.
    ['--pg-eyebrow-size' as never]: `${t.eyebrowSize}px`,
    ['--pg-eyebrow-weight' as never]: String(t.eyebrowWeight),
    ['--pg-eyebrow-tracking' as never]: `${t.eyebrowTracking}em`,
    ['--pg-eyebrow-transform' as never]: t.eyebrowTransform,
    // Default measure for paragraph-carrying blocks (0 = unconstrained).
    ['--pg-body-measure' as never]: t.bodyMaxWidth ? `${t.bodyMaxWidth}px` : 'none',
  }
}

// ════════════════════════════════════════════════════════════════════
// Dark mode
// ════════════════════════════════════════════════════════════════════
// Published pages had no dark mode at all — `modern-dark` is a static
// PRESET, not a mode. Because every color already flows through `--pg-*`
// variables, a mode is just the same variable set re-declared under a media
// query (mode 'auto') and/or a root class (mode 'toggle').
//
// The dark palette is SPARSE: anything the author omits is derived from the
// light palette by the same `derivePalette` contrast math, so a page gets a
// legible dark mode without picking thirty more colors.

/** Resolve a theme's dark palette, deriving whatever the author left out. */
export function resolveDarkPalette(theme: PageTheme | undefined): Required<PalettePreset> | null {
  const dm = theme?.darkMode
  if (!dm || !dm.mode || dm.mode === 'off') return null
  const light = derivePalette(theme?.palette || {})
  // Seed from the author's overrides; when they didn't give a background,
  // invert the light one so the derivation has a dark surface to work from.
  const seed: PalettePreset = { ...(dm.palette || {}) }
  if (!seed.background) seed.background = invertLuminance(light.background)
  // Brand colors carry over unless overridden — a brand doesn't change
  // between modes, only its surroundings do. `derivePalette` then re-checks
  // each one for contrast against the new background and rescues it if the
  // light-mode value would be unreadable on dark.
  if (!seed.primary) seed.primary = light.primary
  if (!seed.accent) seed.accent = light.accent
  return derivePalette(seed)
}

/** The `--pg-*` declaration text for a resolved palette. */
function paletteVarsText(p: Required<PalettePreset>): string {
  const pairs: [string, string][] = [
    ['--pg-primary', p.primary], ['--pg-primary-hover', p.primaryHover], ['--pg-on-primary', p.onPrimary],
    ['--pg-accent', p.accent], ['--pg-accent-hover', p.accentHover], ['--pg-on-accent', p.onAccent],
    ['--pg-heading', p.heading], ['--pg-text', p.text], ['--pg-muted', p.muted], ['--pg-subtle', p.subtle],
    ['--pg-link', p.link], ['--pg-link-hover', p.linkHover], ['--pg-inverted', p.inverted],
    ['--pg-on-surface', p.onSurface], ['--pg-on-surface-alt', p.onSurfaceAlt],
    ['--pg-text-secondary', p.muted],
    ['--pg-bg', p.background], ['--pg-background', p.background],
    ['--pg-surface', p.surface], ['--pg-surface-alt', p.surfaceAlt],
    ['--pg-surface-muted', p.surfaceMuted], ['--pg-elevated', p.elevated],
    ['--pg-border', p.border], ['--pg-border-strong', p.borderStrong],
    ['--pg-info', p.info], ['--pg-info-bg', p.infoBg],
    ['--pg-success', p.success], ['--pg-success-bg', p.successBg],
    ['--pg-warning', p.warning], ['--pg-warning-bg', p.warningBg],
    ['--pg-error', p.error], ['--pg-error-bg', p.errorBg],
  ]
  return pairs.map(([k, v]) => `${k}:${v}`).join(';')
}

/**
 * CSS for a page's dark mode, or '' when it is off.
 *
 * Three selectors, in a deliberate order that matches the artifact-theme
 * convention: the system preference applies unless the visitor has PINNED
 * light, and an explicitly pinned dark always wins. That means a toggle
 * works in both directions instead of only being able to turn dark on.
 */
export function darkModeCss(theme: PageTheme | undefined): string {
  const dark = resolveDarkPalette(theme)
  if (!dark) return ''
  const mode = theme!.darkMode!.mode
  const vars = paletteVarsText(dark)
  let out = ''
  if (mode === 'auto' || mode === 'toggle') {
    out += `@media (prefers-color-scheme: dark){:root:not([data-pg-theme="light"]) .pg-themed{${vars}}}`
  }
  if (mode === 'toggle') {
    out += `:root[data-pg-theme="dark"] .pg-themed{${vars}}`
  }
  return out
}

/**
 * Inline script that applies the visitor's pinned theme BEFORE first paint,
 * so a dark-mode visitor never sees a white flash. Runs in <head> on the
 * static site and from ThemedPageShell on the React surfaces.
 */
export function darkModeBootScript(storageKey = 'pg-theme'): string {
  return `(function(){try{var v=localStorage.getItem(${JSON.stringify(storageKey)});`
    + `if(v==='dark'||v==='light')document.documentElement.setAttribute('data-pg-theme',v);}catch(e){}})();`
}

/** Flip a color to the opposite end of the luminance range. */
function invertLuminance(c: string): string {
  return isColorDark(c) ? '#ffffff' : '#0b1220'
}

// Resolve the effective content width in px: an explicit `maxWidth` override
// wins, else the named preset. Shared by themeCssVars + ThemedPageShell.
export function contentWidthPx(layout: ResolvedTheme['layout']): number {
  return layout.maxWidth ?? CONTENT_WIDTH_PX[layout.contentWidth]
}

// ─── Smart preset switch (preserves user customizations) ─

/**
 * Switch the page from one preset to another while keeping any field the
 * user has explicitly customized. A field counts as "customized" when its
 * current value differs from the OLD preset's default for that field —
 * otherwise it was inherited and gets overwritten with the new preset's
 * value. Works across palette / typography / layout sub-objects.
 *
 * Legacy preset handling: pages saved before the preset rewrite carry
 * old preset ids ('modern' / 'minimal' / 'dark' / etc.) with palettes
 * that match the OLD preset shape. For those, switching presets must
 * always overwrite — `getPreset()` aliases the old id to a new preset,
 * but the saved palette won't match the new preset's defaults, which
 * would make smartMerge mistake every field as "user customized" and
 * silently keep the dark colors when the user clicked Modern Light.
 * The legacy-preset table below holds the original defaults so the
 * comparison sees them as "inherited."
 */
export function smartMergePreset(currentTheme: PageTheme | undefined, newPresetId: string): PageTheme {
  const newPreset = getPreset(newPresetId).theme
  const oldPresetId = currentTheme?.preset || DEFAULT_PRESET_ID
  const oldPreset = LEGACY_PRESET_THEMES[oldPresetId] || getPreset(oldPresetId).theme

  const mergeLeaf = <T extends object>(
    current: T | undefined,
    oldDefaults: T | undefined,
    newDefaults: T | undefined,
  ): T | undefined => {
    if (!newDefaults) return current
    const result: Record<string, unknown> = { ...(newDefaults as object) }
    if (current) {
      const cur = current as Record<string, unknown>
      const old = (oldDefaults ?? {}) as Record<string, unknown>
      for (const key of Object.keys(cur)) {
        if (!isEqual(cur[key], old[key])) result[key] = cur[key]
      }
    }
    return result as T
  }

  return {
    preset: newPresetId,
    palette: mergeLeaf(currentTheme?.palette, oldPreset.palette, newPreset.palette),
    typography: mergeLeaf(currentTheme?.typography, oldPreset.typography, newPreset.typography),
    layout: mergeLeaf(currentTheme?.layout, oldPreset.layout, newPreset.layout),
  }
}

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== 'object' || typeof b !== 'object') return false
  const ak = Object.keys(a as object)
  const bk = Object.keys(b as object)
  if (ak.length !== bk.length) return false
  for (const k of ak) {
    if (!isEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false
  }
  return true
}

// ─── BlockStyle → React.CSSProperties for the wrapper ──

export function blockStyleCss(style: BlockStyle | undefined, blockType?: string): React.CSSProperties {
  if (!style) return {}
  const out: React.CSSProperties = {}
  Object.assign(out, backgroundCss(style.background))
  if (style.textColor) out.color = style.textColor
  const p = style.padding
  if (p) out.padding = `${p.top || 0}px ${p.right || 0}px ${p.bottom || 0}px ${p.left || 0}px`
  const m = style.margin
  // Negative margins are intentional and are how a block overlaps a section
  // boundary (the "designed, not generated" tell). They pass through
  // untouched — the editor clamps only where it must, not here.
  if (m) out.margin = `${m.top || 0}px ${m.right || 0}px ${m.bottom || 0}px ${m.left || 0}px`
  if (style.border) {
    out.border = `${style.border.width}px ${style.border.style || 'solid'} ${style.border.color}`
  }
  if (typeof style.borderRadius === 'number') out.borderRadius = style.borderRadius
  if (style.bleed) {
    // Edge to edge from inside the centred content column: as wide as the
    // viewport, pulled left by the column's offset. `100%` of a centred
    // column minus `100vw`, halved, is exactly that offset on every surface.
    out.width = '100vw'
    out.maxWidth = '100vw'
    out.marginLeft = 'calc(50% - 50vw)'
    out.marginRight = 'calc(50% - 50vw)'
  }
  if (style.shadow && style.shadow !== 'none') out.boxShadow = SHADOWS[style.shadow]
  if (style.align) out.textAlign = style.align
  // ── Layering ──
  if (typeof style.zIndex === 'number') {
    out.zIndex = style.zIndex
    // z-index only applies to a positioned element; a block that asks to be
    // layered has to opt into a stacking context or the value is inert.
    out.position = 'relative'
  }
  if (typeof style.opacity === 'number') out.opacity = style.opacity
  if (typeof style.backdropBlur === 'number' && style.backdropBlur > 0) {
    const blur = `blur(${style.backdropBlur}px)`
    out.backdropFilter = blur
    // Safari still needs the prefix for backdrop-filter.
    ;(out as Record<string, unknown>).WebkitBackdropFilter = blur
  }
  // ── Per-block typography ──
  Object.assign(out, blockTypographyCss(style.typography))
  // A heading / paragraph sets its own size on its element (the theme scale,
  // a default), so the wrapper's font-size never reached it and the block's
  // size — desktop, tablet or phone — was ignored. Hand it over as the
  // variable those elements read first. Text blocks only: on a container the
  // variable would be inherited by every heading nested inside it.
  if (blockType && OWN_SIZE_TEXT_BLOCKS.has(blockType) && typeof style.typography?.fontSize === 'number') {
    (out as Record<string, string>)['--pg-text-size'] = `${style.typography.fontSize}px`
  }
  // NOTE: the hover TRANSITION is deliberately NOT set here. It has to land on
  // whatever the hover actually targets — the wrapper, or each `.pg-item` —
  // and only `blockHoverCss` knows the scope. Emitting it on the wrapper as
  // well would animate the grid container during an item-scoped hover.
  return out
}

/** Text blocks whose element reads the block's own size (`--pg-text-size`). */
const OWN_SIZE_TEXT_BLOCKS = new Set(['heading', 'paragraph'])

/** Per-block typography overrides → CSS. */
export function blockTypographyCss(t: BlockStyle['typography']): React.CSSProperties {
  if (!t) return {}
  const out: React.CSSProperties = {}
  if (typeof t.fontSize === 'number') out.fontSize = `${t.fontSize}px`
  if (typeof t.fontWeight === 'number') out.fontWeight = t.fontWeight
  if (typeof t.letterSpacing === 'number') out.letterSpacing = `${t.letterSpacing}em`
  if (typeof t.lineHeight === 'number') out.lineHeight = t.lineHeight
  if (t.textTransform && t.textTransform !== 'none') out.textTransform = t.textTransform
  if (t.fontFamily) out.fontFamily = FONT_STACKS[t.fontFamily]
  // A named family (a cloned site's Verdana) wins, with the preset stack
  // kept as its fallback chain.
  if (t.customFontFamily && t.customFontFamily.trim()) {
    out.fontFamily = `"${t.customFontFamily.trim().replace(/"/g, '')}", ${FONT_STACKS[t.fontFamily || 'sans']}`
  }
  if (typeof t.maxWidth === 'number') out.maxWidth = `${t.maxWidth}px`
  if (t.textGradient) {
    // `background-clip: text` paints the glyphs with the gradient. The
    // element keeps a real `color` underneath so a browser without support
    // (or a copy-paste) still shows readable text rather than nothing.
    const g = t.textGradient
    out.backgroundImage = `linear-gradient(${g.angle ?? 90}deg, ${g.from}, ${g.to})`
    ;(out as Record<string, unknown>).WebkitBackgroundClip = 'text'
    out.backgroundClip = 'text'
    ;(out as Record<string, unknown>).WebkitTextFillColor = 'transparent'
  }
  return out
}

/**
 * A hover/focus state → CSS declaration text.
 *
 * `:hover` is inexpressible inline, so any block carrying `style.hover` is
 * rendered through the wrapper's scoped-`<style>` path (the same path
 * responsive overrides already use). `:focus-within` rides along so the
 * effect is reachable by keyboard, not just by mouse.
 */
export function blockStateCssText(state: BlockStyleState | undefined): string {
  if (!state) return ''
  const css: React.CSSProperties = {}
  Object.assign(css, backgroundCss(state.background))
  if (state.textColor) css.color = state.textColor
  if (state.border) {
    css.border = `${state.border.width}px ${state.border.style || 'solid'} ${state.border.color}`
  }
  if (typeof state.borderRadius === 'number') css.borderRadius = state.borderRadius
  if (state.shadow) css.boxShadow = state.shadow === 'none' ? 'none' : SHADOWS[state.shadow]
  if (typeof state.opacity === 'number') css.opacity = state.opacity
  if (typeof state.backdropBlur === 'number') {
    css.backdropFilter = `blur(${state.backdropBlur}px)`
    ;(css as Record<string, unknown>).WebkitBackdropFilter = `blur(${state.backdropBlur}px)`
  }
  const tf: string[] = []
  if (typeof state.translateY === 'number') tf.push(`translateY(${state.translateY}px)`)
  if (typeof state.scale === 'number') tf.push(`scale(${state.scale})`)
  if (tf.length) css.transform = tf.join(' ')
  return cssPropsToText(css)
}

/** True when the block has any state that must be emitted as real CSS. */
export function hasHover(style: BlockStyle | undefined): boolean {
  return !!(style?.hover && Object.keys(style.hover).length > 0)
}

/**
 * The class every repeated element of a multi-element block carries.
 * `hoverScope: 'items'` targets it. See `ITEM_BEARING_BLOCKS`.
 */
export const ITEM_CLASS = 'pg-item'

/**
 * The base selectors a block's hover state applies to, given its scope.
 *
 * `'items'` is the one that matters: a `card_grid` is ONE block containing
 * many cards, so a wrapper-scoped hover lifts the whole grid rather than the
 * card under the cursor. Scoping to `.pg-item` makes each repeated element
 * respond on its own.
 */
function hoverTargets(selector: string, scope: HoverScope): string[] {
  const wrapper = `.${selector}`
  const item = `.${selector} .${ITEM_CLASS}`
  if (scope === 'items') return [item]
  if (scope === 'both') return [wrapper, item]
  return [wrapper]
}

/**
 * The effective hover scope for a block.
 *
 * An explicit `hoverScope` always wins. When it is absent the default depends
 * on the BLOCK TYPE: a block that renders repeated elements defaults to
 * `'items'`, because scoping a card grid's hover to its wrapper — lifting all
 * nine cards whenever the cursor touches any of them — is a defect, not a
 * choice anyone makes deliberately. Single-unit blocks default to `'block'`,
 * which is the only thing that means anything for them.
 */
export function resolveHoverScope(
  style: BlockStyle | undefined,
  blockType?: string,
): HoverScope {
  if (style?.hoverScope) return style.hoverScope
  return blockType && blockHasItems(blockType) ? 'items' : 'block'
}

/**
 * `:hover` + `:focus-within` rules for a block class.
 *
 * Transform-based hovers are additionally wrapped in the reduced-motion
 * guard: a visitor who asked for less motion still gets the color/shadow
 * change (a useful affordance) but not the movement.
 */
export function blockHoverCss(
  selector: string,
  style: BlockStyle | undefined,
  blockType?: string,
): string {
  if (!hasHover(style)) return ''
  const decls = blockStateCssText(style!.hover)
  if (!decls) return ''
  const targets = hoverTargets(selector, resolveHoverScope(style, blockType))
  // `:focus-within` rides along so the effect is reachable by keyboard, not
  // just by mouse.
  const sel = targets.flatMap((t) => [`${t}:hover`, `${t}:focus-within`]).join(',')

  // The transition belongs on the RESTING element, and for item scope that is
  // each item — not the wrapper, which never changes.
  const restSel = targets.join(',')
  const ms = clampMs(style!.transition, 200, 0, 2000)
  const base = `${restSel}{transition:all ${ms}ms ease}`

  const moves = /transform:/.test(decls)
  if (!moves) return `${base}${sel}{${decls}}`
  // Split so the non-motion half always applies under reduced motion.
  const still = decls.replace(/transform:[^;]*;?/g, '').replace(/;$/, '')
  const move = (decls.match(/transform:[^;]*/) || [''])[0]
  return base
    + (still ? `${sel}{${still}}` : '')
    + `@media (prefers-reduced-motion: no-preference){${sel}{${move}}}`
}

// ── Tooltips ──────────────────────────────────────────────
// Pure CSS (no JS, so it survives on the static site), revealed on
// :hover/:focus-within, and driven entirely by data attributes so the
// content lives in the DOM where a screen reader can reach it.
export const TOOLTIP_CSS = `
.pg-tip{position:relative}
.pg-tip > .pg-tip-bub{
  position:absolute;z-index:60;pointer-events:none;opacity:0;
  max-width:260px;width:max-content;padding:8px 10px;border-radius:8px;
  background:var(--pg-elevated,#111827);color:var(--pg-inverted,#fff);
  font-size:.78rem;line-height:1.45;text-align:left;
  box-shadow:0 8px 24px rgba(0,0,0,.24);
  transition:opacity .15s ease, transform .15s ease;
}
.pg-tip > .pg-tip-bub b{display:block;font-weight:700}
.pg-tip[data-tip-place="top"] > .pg-tip-bub{bottom:calc(100% + 8px);left:50%;transform:translate(-50%,4px)}
.pg-tip[data-tip-place="bottom"] > .pg-tip-bub{top:calc(100% + 8px);left:50%;transform:translate(-50%,-4px)}
.pg-tip[data-tip-place="left"] > .pg-tip-bub{right:calc(100% + 8px);top:50%;transform:translate(4px,-50%)}
.pg-tip[data-tip-place="right"] > .pg-tip-bub{left:calc(100% + 8px);top:50%;transform:translate(-4px,-50%)}
.pg-tip:hover > .pg-tip-bub,.pg-tip:focus-within > .pg-tip-bub{opacity:1}
.pg-tip[data-tip-place="top"]:hover > .pg-tip-bub,.pg-tip[data-tip-place="top"]:focus-within > .pg-tip-bub{transform:translate(-50%,0)}
.pg-tip[data-tip-place="bottom"]:hover > .pg-tip-bub,.pg-tip[data-tip-place="bottom"]:focus-within > .pg-tip-bub{transform:translate(-50%,0)}
.pg-tip[data-tip-place="left"]:hover > .pg-tip-bub,.pg-tip[data-tip-place="left"]:focus-within > .pg-tip-bub{transform:translate(0,-50%)}
.pg-tip[data-tip-place="right"]:hover > .pg-tip-bub,.pg-tip[data-tip-place="right"]:focus-within > .pg-tip-bub{transform:translate(0,-50%)}
`

export function hasTooltip(tip: BlockTooltip | undefined): boolean {
  return !!(tip && tip.title)
}

// ════════════════════════════════════════════════════════════════════
// Responsive per-breakpoint styles
// --------------------------------------------------------------------
// A block's `style` is the base (desktop / all widths). `responsive.tablet`
// and `responsive.mobile` carry only the fields that differ at narrower
// widths. Two consumption modes:
//   • Designer canvas — resolve the merged style for the device being
//     previewed and apply it inline (`resolveBlockStyleFor`). The canvas
//     is width-constrained, so real @media rules wouldn't fire there.
//   • Deployed runtime / SSR — emit real @media CSS (`blockResponsiveCss`)
//     so the page reflows on actual devices.
// Cascade is CSS-like: mobile inherits tablet inherits desktop.
// ════════════════════════════════════════════════════════════════════

// CSS max-width breakpoints for the two override layers. Chosen to match
// common device classes (tablet portrait ≤ 1024, phones ≤ 640). The
// designer's DeviceToggle uses narrower exact widths for its frame; that
// only affects the simulated canvas width, not these emitted rules.
export const RESPONSIVE_BREAKPOINTS = { tablet: 1024, mobile: 640 } as const

export function hasResponsive(responsive: ResponsiveStyle | undefined): boolean {
  return !!(responsive && (responsive.tablet || responsive.mobile))
}

// Shallow-merge an override layer over a base. Each field in the override
// (when present) replaces the base field entirely — partial overrides are
// expressed by simply omitting fields. Undefined override → base unchanged.
export function mergeBlockStyle(base: BlockStyle | undefined, override: BlockStyle | undefined): BlockStyle {
  if (!override) return base ?? {}
  return { ...(base ?? {}), ...override }
}

// Resolve the effective style for a given viewport, applying the cascade.
export function resolveBlockStyleFor(
  block: { style?: BlockStyle; responsive?: ResponsiveStyle },
  viewport: Viewport,
): BlockStyle | undefined {
  const base = block.style
  if (viewport === 'desktop' || !block.responsive) return base
  const tablet = mergeBlockStyle(base, block.responsive.tablet)
  if (viewport === 'tablet') return tablet
  return mergeBlockStyle(tablet, block.responsive.mobile)
}

// Read the raw override layer for the editor UI (so unset fields show as
// empty rather than the inherited value).
export function blockStyleLayer(
  block: { style?: BlockStyle; responsive?: ResponsiveStyle },
  viewport: Viewport,
): BlockStyle | undefined {
  if (viewport === 'desktop') return block.style
  return block.responsive?.[viewport]
}

// Write a style layer back onto a block for the editor UI. Desktop writes
// `block.style`; tablet/mobile write `block.responsive[viewport]`. Empty
// layers are pruned (style → undefined, responsive → undefined when both
// breakpoints clear) so persisted JSON stays minimal.
export function setBlockStyleLayer<T extends { style?: BlockStyle; responsive?: ResponsiveStyle }>(
  block: T,
  viewport: Viewport,
  style: BlockStyle | undefined,
): T {
  const pruned = style && Object.keys(style).length ? style : undefined
  if (viewport === 'desktop') {
    return { ...block, style: pruned }
  }
  const resp: ResponsiveStyle = { ...(block.responsive ?? {}) }
  if (pruned) resp[viewport] = pruned
  else delete resp[viewport]
  return { ...block, responsive: resp.tablet || resp.mobile ? resp : undefined }
}

// camelCase React.CSSProperties → CSS declaration text. Only `borderRadius`
// is numeric in blockStyleCss output, so px-suffix it; everything else is
// already a string.
function cssPropsToText(css: React.CSSProperties): string {
  return Object.entries(css)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => {
      const prop = k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())
      const val = typeof v === 'number' && k === 'borderRadius' ? `${v}px` : v
      return `${prop}:${val}`
    })
    .join(';')
}

export function blockStyleCssText(style: BlockStyle | undefined, blockType?: string): string {
  return cssPropsToText(blockStyleCss(style, blockType))
}

// Emit `<style>`-ready CSS for a block that has responsive overrides:
// base rules on the class, then @media blocks for tablet/mobile. Returns
// '' when the block has no responsive overrides (caller stays inline).
// `selector` is the per-block class WITHOUT the leading dot.
export function blockResponsiveCss(
  selector: string,
  style: BlockStyle | undefined,
  responsive: ResponsiveStyle | undefined,
): string {
  if (!hasResponsive(responsive)) return ''
  const sel = `.${selector}`
  let out = ''
  const base = blockStyleCssText(style)
  if (base) out += `${sel}{${base}}`
  // Tablet first, mobile second — at phone widths both queries match and
  // the later (mobile) rule wins, matching resolveBlockStyleFor's cascade.
  const tablet = blockStyleCssText(responsive!.tablet)
  if (tablet) out += `@media (max-width:${RESPONSIVE_BREAKPOINTS.tablet}px){${sel}{${tablet}}}`
  const mobile = blockStyleCssText(responsive!.mobile)
  if (mobile) out += `@media (max-width:${RESPONSIVE_BREAKPOINTS.mobile}px){${sel}{${mobile}}}`
  return out
}

/**
 * True when a block needs the scoped-`<style>` path rather than plain inline
 * styles. Inline styles cannot express `@media` or `:hover`, and inline
 * specificity would also outrank the override rules — so responsive layers
 * and interaction states both force the class path.
 */
export function needsScopedCss(
  style: BlockStyle | undefined,
  responsive: ResponsiveStyle | undefined,
): boolean {
  return hasResponsive(responsive) || hasHover(style)
}

/**
 * THE per-block CSS emitter: base declarations, responsive `@media` layers
 * and interaction states, all scoped to one generated class.
 *
 * This is the single implementation every off-canvas surface uses (the
 * shared `StyledBlock` calls it). Before this existed, each surface
 * hand-rolled its own subset — which is exactly why `:hover` and per-block
 * typography could not be added without writing them three times.
 */
export function blockRulesCss(
  selector: string,
  style: BlockStyle | undefined,
  responsive: ResponsiveStyle | undefined,
  blockType?: string,
): string {
  const sel = `.${selector}`
  let out = ''
  const base = blockStyleCssText(style, blockType)
  if (base) out += `${sel}{${base}}`
  // `blockType` decides whether an unset hover scope means the wrapper or
  // each repeated element — see resolveHoverScope.
  out += blockHoverCss(selector, style, blockType)
  if (hasResponsive(responsive)) {
    const tablet = blockStyleCssText(responsive!.tablet, blockType)
    if (tablet) out += `@media (max-width:${RESPONSIVE_BREAKPOINTS.tablet}px){${sel}{${tablet}}}`
    const mobile = blockStyleCssText(responsive!.mobile, blockType)
    if (mobile) out += `@media (max-width:${RESPONSIVE_BREAKPOINTS.mobile}px){${sel}{${mobile}}}`
  }
  return out
}

// ════════════════════════════════════════════════════════════════════
// Section layout: width, height, dividers, decorations, sticky
// ════════════════════════════════════════════════════════════════════

/**
 * Global rules for the section layout primitives. Injected once per page
 * alongside ANIMATION_CSS.
 *
 * The full-bleed technique: a section inside the centered content column
 * escapes it with symmetric negative margins computed from the column width,
 * then re-applies the page's horizontal padding to its own CONTENT. This
 * keeps the background edge-to-edge while the text stays on the same
 * measure as every other section — the standard "full-bleed band".
 */
export const SECTION_LAYOUT_CSS = `
.pg-sec{position:relative}
.pg-sec-full{
  width:100vw;
  margin-left:calc(50% - 50vw);
  margin-right:calc(50% - 50vw);
  max-width:100vw;
}
.pg-sec-full > .pg-sec-inner{
  max-width:var(--pg-content-width);
  margin:0 auto;
  width:100%;
  padding-left:var(--pg-page-padding-x);
  padding-right:var(--pg-page-padding-x);
  box-sizing:border-box;
}
/* Two full-bleed bands in a row touch, the way a real marketing page stacks
   its colour bands — the column's section gap would otherwise show a strip of
   page background between them. --pg-sec-gap is the host stack's gap (the
   in-app runtime sets it); the published shell stacks on --pg-block-gap. */
.pg-sec-full + .pg-sec-full{margin-top:calc(-1 * var(--pg-sec-gap, var(--pg-block-gap, 0px)))}
.pg-sec-breakout{
  width:min(100vw, calc(var(--pg-content-width) * 1.18));
  margin-left:50%;
  transform:translateX(-50%);
  max-width:100vw;
}
.pg-sec-tall{display:flex;flex-direction:column}
.pg-sec-tall > .pg-sec-inner{width:100%}
.pg-sec-v-center{justify-content:center}
.pg-sec-v-bottom{justify-content:flex-end}
.pg-sec-v-top{justify-content:flex-start}
.pg-sec-sticky{position:sticky;top:var(--pg-sec-sticky-top,0px);z-index:20}

/* Shape dividers sit on the section's edges and never intercept clicks. */
.pg-div{position:absolute;left:0;width:100%;line-height:0;pointer-events:none;z-index:1;overflow:hidden}
.pg-div-top{top:0}
.pg-div-bottom{bottom:0}
.pg-div svg{display:block;width:100%;height:100%}
.pg-div-above{z-index:3}

/* Decorative layers — purely presentational, hidden from assistive tech. */
.pg-deco{position:absolute;pointer-events:none;z-index:0}
.pg-deco-blob,.pg-deco-glow{border-radius:50%}
.pg-sec > .pg-sec-inner,.pg-sec > .pg-sec-body{position:relative;z-index:2}
`

/** SVG path data for each shape divider, drawn in a 0 0 1200 120 viewBox. */
const DIVIDER_PATHS: Record<string, string> = {
  wave: 'M0,64 C150,110 350,10 600,54 C850,98 1050,20 1200,54 L1200,120 L0,120 Z',
  slant: 'M0,120 L1200,0 L1200,120 Z',
  curve: 'M0,120 C300,0 900,0 1200,120 Z',
  triangle: 'M600,0 L1200,120 L0,120 Z',
  arrow: 'M0,0 L600,90 L1200,0 L1200,120 L0,120 Z',
  zigzag: 'M0,120 L0,40 L150,90 L300,40 L450,90 L600,40 L750,90 L900,40 L1050,90 L1200,40 L1200,120 Z',
  book: 'M0,120 C200,40 400,40 600,80 C800,120 1000,120 1200,60 L1200,120 Z',
}

export function dividerPath(kind: ShapeDivider['kind']): string | null {
  if (!kind || kind === 'none') return null
  return DIVIDER_PATHS[kind] || null
}

/**
 * `Section.lazy` — defer the rendering COST of an off-screen section.
 *
 * The field has been in the schema since v2 with no implementation, so an
 * author who ticked it got nothing. It is implemented with CSS
 * `content-visibility: auto` rather than an IntersectionObserver-driven
 * mount, deliberately:
 *
 *   • the section's content stays in the DOM, so it is crawlable, findable
 *     with in-page search, and reachable by an anchor link — everything an
 *     observer-mounted section would break for a marketing page, and
 *   • `contain-intrinsic-size` reserves a plausible box so the scrollbar
 *     doesn't jump as sections come into view.
 *
 * Browsers without support simply render normally, which is the correct
 * degradation for a pure performance hint.
 */
export const LAZY_SECTION_CSS = `
.pg-sec-lazy{content-visibility:auto;contain-intrinsic-size:auto 600px}
`

/** The wrapper class list a section carries for its layout options. */
export function sectionLayoutClasses(section: Section): string[] {
  const out = ['pg-sec']
  if (section.lazy) out.push('pg-sec-lazy')
  const w = section.width || 'contained'
  if (w === 'full') out.push('pg-sec-full')
  else if (w === 'breakout') out.push('pg-sec-breakout')
  if (section.minHeight) {
    out.push('pg-sec-tall')
    out.push(`pg-sec-v-${section.verticalAlign || 'center'}`)
  }
  if (section.sticky) out.push('pg-sec-sticky')
  return out
}

/**
 * The horizontal inset a section's content gets when nobody set one: the
 * page's own side padding, capped at 5vw so a phone keeps its width, and
 * never under 16px so text never touches an edge.
 */
export const SECTION_DEFAULT_INSET_X = 'max(16px, min(var(--pg-page-padding-x, 32px), 5vw))'

/**
 * Does the section paint a surface of its own — a fill, a border, or a
 * colour scheme? Only then is its edge visible, and only then does content
 * sitting flush against that edge look broken.
 */
export function sectionPaintsSurface(section: Section, hasScheme: boolean): boolean {
  if (hasScheme) return true
  const s = section.style
  if (!s) return false
  const kind = s.background?.kind
  if (kind && kind !== 'none' && kind !== 'transparent') return true
  return !!(s.border && (s.border.width ?? 0) > 0)
}

/**
 * Default side padding for a section whose surface would otherwise run
 * right up against its content (a coloured band with only top/bottom
 * padding). Applied by every section renderer, so no author (or agent) has
 * to remember it. An explicit left/right padding (0 included) always wins; a
 * surface-less section gets nothing, because its edge is invisible.
 */
export function sectionDefaultInset(section: Section, hasScheme: boolean): React.CSSProperties {
  if (!sectionPaintsSurface(section, hasScheme)) return {}
  const p = section.style?.padding || {}
  // A side the author set — even to 0, for edge-to-edge content — is kept.
  const isSet = (v?: number) => typeof v === 'number'
  if (isSet(p.left) && isSet(p.right)) return {}
  // The whole shorthand, so it replaces blockStyleCss's `padding` cleanly
  // (mixing `padding` with `paddingLeft` makes React warn on re-render).
  const side = (v?: number) => (isSet(v) ? `${v}px` : SECTION_DEFAULT_INSET_X)
  return { padding: `${p.top || 0}px ${side(p.right)} ${p.bottom || 0}px ${side(p.left)}` }
}

/** Inline style bits a section's layout options need (heights, offsets). */
export function sectionLayoutStyle(section: Section): React.CSSProperties {
  const out: React.CSSProperties = {}
  if (section.minHeight === 'screen') {
    // `svh` tracks the small viewport height, so a mobile browser's
    // collapsing address bar doesn't leave a scroll-jump gap. `vh` stays as
    // the fallback for browsers without svh.
    out.minHeight = '100vh'
    ;(out as Record<string, unknown>).minHeight = '100svh'
  } else if (typeof section.minHeight === 'number') {
    out.minHeight = `${section.minHeight}px`
  }
  if (section.sticky && typeof section.stickyOffset === 'number') {
    ;(out as Record<string, string>)['--pg-sec-sticky-top'] = `${section.stickyOffset}px`
  }
  // A section that paints decorations or dividers must clip them to itself,
  // unless a divider was asked to overhang.
  if ((section.decorations && section.decorations.length) || section.dividerTop || section.dividerBottom) {
    out.overflow = 'hidden'
  }
  return out
}

/** Absolute placement + paint for one decorative layer. */
export function decorationStyle(d: Decoration): React.CSSProperties {
  const size = d.size ?? 320
  const color = d.color || 'var(--pg-primary)'
  const opacity = d.opacity ?? 0.16
  const corner = d.corner || 'top-right'
  const dx = d.offsetX ?? 0
  const dy = d.offsetY ?? 0
  // Anchor to the requested corner, pulled a third of its own size outside
  // the box so the shape bleeds off the edge instead of floating in frame.
  const bleed = -Math.round(size / 3)
  const pos: React.CSSProperties = corner === 'center'
    ? { top: '50%', left: '50%', transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))` }
    : {
        ...(corner.startsWith('top') ? { top: bleed + dy } : { bottom: bleed - dy }),
        ...(corner.endsWith('left') ? { left: bleed + dx } : { right: bleed - dx }),
      }
  const base: React.CSSProperties = { ...pos, width: size, height: size, opacity }

  switch (d.kind) {
    case 'glow':
      return { ...base, background: color, filter: `blur(${d.blur ?? 90}px)`, borderRadius: '50%' }
    case 'blob':
      return {
        ...base, background: color, filter: `blur(${d.blur ?? 60}px)`,
        // An irregular radius is what separates a "blob" from a circle.
        borderRadius: '42% 58% 63% 37% / 45% 38% 62% 55%',
      }
    case 'dot-grid':
      return {
        ...base,
        backgroundImage: `radial-gradient(${color} 1.5px, transparent 1.5px)`,
        backgroundSize: '18px 18px',
        // Fade the grid out toward its far edge so it reads as a texture
        // rather than a hard-edged rectangle.
        maskImage: 'radial-gradient(closest-side, #000 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(closest-side, #000 40%, transparent 100%)',
      } as React.CSSProperties
    case 'grid-lines':
      return {
        ...base,
        backgroundImage:
          `linear-gradient(${color} 1px, transparent 1px),` +
          `linear-gradient(90deg, ${color} 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
        maskImage: 'radial-gradient(closest-side, #000 30%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(closest-side, #000 30%, transparent 100%)',
      } as React.CSSProperties
    case 'rings':
      return {
        ...base,
        border: `1px solid ${color}`,
        borderRadius: '50%',
        boxShadow: `0 0 0 ${Math.round(size * 0.18)}px transparent, inset 0 0 0 ${Math.round(size * 0.16)}px ${color}00, 0 0 0 1px ${color}`,
        background: `radial-gradient(circle, transparent 55%, ${color}22 56%, transparent 58%)`,
      }
    case 'noise':
    default:
      return {
        ...base,
        // A tiny inline SVG turbulence — no network request, no asset.
        backgroundImage:
          `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E")`,
        borderRadius: 'inherit',
      }
  }
}

// ════════════════════════════════════════════════════════════════════
// Media helpers — aspect locking, object fit, masks, hover treatments
// ════════════════════════════════════════════════════════════════════

/** `MediaAspect` → a CSS `aspect-ratio` value ('auto' → undefined). */
export function aspectRatioCss(aspect: string | undefined): string | undefined {
  if (!aspect || aspect === 'auto') return undefined
  const [w, h] = aspect.split(':').map(Number)
  if (!w || !h) return undefined
  return `${w} / ${h}`
}

/** Decorative `clip-path` shapes for images. */
export const IMAGE_MASKS: Record<string, string | undefined> = {
  none: undefined,
  circle: 'circle(50% at 50% 50%)',
  squircle: 'inset(0 round 28%)',
  arch: 'inset(0 round 50% 50% 0 0)',
  diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  hexagon: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
  // An organic silhouette approximated with a many-sided polygon — a real
  // blob needs an SVG path, but this reads the same at typical sizes.
  blob: 'polygon(38% 2%, 68% 5%, 90% 22%, 98% 50%, 90% 78%, 66% 96%, 36% 98%, 12% 82%, 2% 52%, 8% 22%)',
}

/**
 * Image hover treatments. Emitted as real CSS against the block class, and
 * every transform-based effect is inside the reduced-motion guard so a
 * visitor who asked for less motion keeps a still image.
 */
export function imageHoverCss(selector: string, effect: string | undefined): string {
  if (!effect || effect === 'none') return ''
  const sel = `.${selector}`
  const img = `${sel} img`
  switch (effect) {
    case 'zoom':
      return `${sel}{overflow:hidden}${img}{transition:transform .5s cubic-bezier(.2,.8,.2,1)}`
        + `@media (prefers-reduced-motion: no-preference){${sel}:hover img,${sel}:focus-within img{transform:scale(1.06)}}`
    case 'lift':
      return `${sel}{transition:transform .25s ease, box-shadow .25s ease}`
        + `${sel}:hover,${sel}:focus-within{box-shadow:0 18px 40px rgba(0,0,0,.18)}`
        + `@media (prefers-reduced-motion: no-preference){${sel}:hover,${sel}:focus-within{transform:translateY(-4px)}}`
    case 'tilt':
      return `${sel}{transform-style:preserve-3d;transition:transform .3s ease}`
        + `@media (prefers-reduced-motion: no-preference){${sel}:hover,${sel}:focus-within{transform:perspective(800px) rotateX(3deg) rotateY(-4deg) scale(1.02)}}`
    case 'grayscale-off':
      // Starts desaturated and returns to full color on hover — reads as
      // intentional even without motion, so no guard is needed.
      return `${img}{filter:grayscale(100%);transition:filter .35s ease}`
        + `${sel}:hover img,${sel}:focus-within img{filter:grayscale(0)}`
    case 'reveal-caption':
      return `${sel}{overflow:hidden;position:relative}`
        + `${sel} figcaption{position:absolute;left:0;right:0;bottom:0;margin:0;padding:12px 14px;`
        + `background:linear-gradient(transparent, rgba(0,0,0,.72));color:#fff;text-align:left;`
        + `opacity:0;transform:translateY(8px);transition:opacity .25s ease, transform .25s ease}`
        + `${sel}:hover figcaption,${sel}:focus-within figcaption{opacity:1;transform:none}`
    default:
      return ''
  }
}

// ════════════════════════════════════════════════════════════════════
// Marquee (logo ticker / scrolling strip)
// ════════════════════════════════════════════════════════════════════
// Pure CSS keyframes so the static site gets it. The track holds TWO copies
// of the item list and translates by exactly -50%, which is what makes the
// loop seamless rather than snapping back.
export const MARQUEE_CSS = `
.pg-marquee{overflow:hidden;position:relative;width:100%}
.pg-marquee-fade{
  -webkit-mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent);
  mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent);
}
.pg-marquee-track{display:flex;align-items:center;gap:var(--pg-mq-gap,48px);width:max-content}
.pg-marquee-track > *{flex:0 0 auto}
@media (prefers-reduced-motion: no-preference){
  @keyframes pg-mq{from{transform:translateX(0)}to{transform:translateX(-50%)}}
  .pg-marquee-track{animation:pg-mq var(--pg-mq-speed,30s) linear infinite}
  .pg-marquee-rev .pg-marquee-track{animation-direction:reverse}
  .pg-marquee-pause:hover .pg-marquee-track{animation-play-state:paused}
}
/* Under reduced motion the track doesn't scroll, so the duplicate copy
   would just be visible clutter — hide it and let the row wrap normally. */
@media (prefers-reduced-motion: reduce){
  .pg-marquee-track{flex-wrap:wrap;width:100%;justify-content:center}
  .pg-marquee-track [data-pg-mq-clone]{display:none}
}
`

// ════════════════════════════════════════════════════════════════════
// Page chrome: scroll progress, back-to-top, smooth scroll
// ════════════════════════════════════════════════════════════════════
export const PAGE_CHROME_CSS = `
.pg-progress{
  position:fixed;top:0;left:0;height:var(--pg-progress-h,3px);width:100%;
  transform-origin:0 50%;transform:scaleX(0);z-index:900;
  background:var(--pg-progress-color,var(--pg-primary));pointer-events:none;
}
.pg-to-top{
  position:fixed;z-index:880;width:44px;height:44px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;cursor:pointer;
  border:1px solid var(--pg-border);background:var(--pg-surface);color:var(--pg-text);
  box-shadow:0 8px 24px rgba(0,0,0,.18);
  opacity:0;visibility:hidden;transition:opacity .2s ease, visibility .2s ease, transform .2s ease;
}
.pg-to-top[data-visible]{opacity:1;visibility:visible}
.pg-to-top-br{right:24px;bottom:24px}
.pg-to-top-bl{left:24px;bottom:24px}
@media (prefers-reduced-motion: no-preference){
  .pg-to-top:hover{transform:translateY(-2px)}
}
/* Anchor targets clear a sticky header instead of hiding beneath it. */
[id]{scroll-margin-top:var(--pg-anchor-offset,80px)}
@media (prefers-reduced-motion: no-preference){
  html.pg-smooth{scroll-behavior:smooth}
}
`

/**
 * Drives the scroll-progress bar and the back-to-top button. Like the reveal
 * script this is plain vanilla JS emitted once per page, so it works on the
 * static site where a React effect would not.
 */
export function pageChromeScript(): string {
  return `(function(){
  if(window.__pgChrome)return;window.__pgChrome=1;
  var bar=document.querySelector('.pg-progress');
  var top=document.querySelector('.pg-to-top');
  var after=top?parseInt(top.getAttribute('data-after')||'600',10):0;
  function onScroll(){
    var y=window.scrollY||document.documentElement.scrollTop||0;
    if(bar){
      var h=document.documentElement.scrollHeight-window.innerHeight;
      bar.style.transform='scaleX('+(h>0?Math.min(1,y/h):0)+')';
    }
    if(top){ if(y>after)top.setAttribute('data-visible','');else top.removeAttribute('data-visible'); }
  }
  if(top){
    top.addEventListener('click',function(){
      var reduce=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({top:0,behavior:reduce?'auto':'smooth'});
    });
  }
  window.addEventListener('scroll',onScroll,{passive:true});
  window.addEventListener('resize',onScroll,{passive:true});
  onScroll();
})();`
}

// ════════════════════════════════════════════════════════════════════
// Navigation: mobile drawer, mega-menu, active link
// ════════════════════════════════════════════════════════════════════
// The drawer is CSS-FIRST on purpose. A hidden checkbox holds the open
// state, so it opens and closes on the STATIC published site with no
// JavaScript at all — the site's mobile navigation cannot be broken by a
// script that fails to load. `navDrawerScript()` only layers on the polish
// (Escape to close, scroll lock, close-after-navigation).
//
// Before this existed there was NO mobile navigation in the page runtime:
// `NavMenuRenderer` simply `flex-wrap`ped its links, so every published site
// failed the first phone visit.
export const NAV_CSS = `
.pg-nav-toggle{position:absolute;opacity:0;width:1px;height:1px;pointer-events:none}
.pg-nav-burger{display:none;align-items:center;justify-content:center;
  width:42px;height:42px;border-radius:10px;cursor:pointer;color:inherit;
  border:1px solid var(--pg-border);background:transparent}
.pg-nav-burger span{display:block;width:18px;height:2px;background:currentColor;position:relative}
.pg-nav-burger span::before,.pg-nav-burger span::after{
  content:'';position:absolute;left:0;width:18px;height:2px;background:currentColor}
.pg-nav-burger span::before{top:-6px}
.pg-nav-burger span::after{top:6px}
.pg-nav-scrim{display:none}

/* Active / current link affordance (set by scrollSpyScript). */
.pg-navmenu a[data-pg-active],.pg-navmenu a[data-pg-current]{
  color:var(--pg-primary);font-weight:700}
.pg-navmenu a[data-pg-current]{box-shadow:inset 0 -2px 0 var(--pg-primary)}

/* ── Mega-menu panel ── */
.pg-navmenu-dropdown.pg-mega{
  min-width:min(760px,90vw);
  display:none;grid-template-columns:repeat(var(--pg-mega-cols,3),minmax(0,1fr));
  gap:4px;padding:14px;left:0;
}
.pg-navmenu-item:hover > .pg-navmenu-dropdown.pg-mega{display:grid!important}
.pg-mega-item{display:flex;gap:10px;align-items:flex-start;padding:10px;border-radius:8px;
  text-decoration:none;color:inherit}
.pg-mega-item:hover{background:color-mix(in srgb,currentColor 8%,transparent)}
.pg-mega-item .pg-mega-t{font-weight:600;display:block}
.pg-mega-item .pg-mega-d{font-size:.78rem;opacity:.72;display:block;margin-top:2px;line-height:1.4}
.pg-mega-badge{font-size:.62rem;font-weight:700;text-transform:uppercase;letter-spacing:.05em;
  padding:2px 6px;border-radius:999px;background:var(--pg-info-bg);color:var(--pg-accent);margin-left:6px}
`

/**
 * The drawer's responsive half — scoped to ONE menu's breakpoint, because a
 * header and a footer nav on the same page may legitimately collapse at
 * different widths. Emitted per nav_menu block alongside its markup.
 */
export function navDrawerCss(cls: string, breakpoint: number, side: 'left' | 'right' | 'full'): string {
  const width = side === 'full' ? '100%' : 'min(360px, 86vw)'
  const from = side === 'right' ? 'translateX(100%)' : side === 'left' ? 'translateX(-100%)' : 'translateY(-100%)'
  const anchor = side === 'right' ? 'right:0;top:0;bottom:0;' : side === 'left' ? 'left:0;top:0;bottom:0;' : 'inset:0;'
  return `@media (max-width:${breakpoint}px){
  .${cls} .pg-nav-burger{display:inline-flex}
  .${cls} .pg-nav-list{
    position:fixed;z-index:1000;${anchor}width:${width};
    flex-direction:column!important;align-items:stretch!important;flex-wrap:nowrap!important;
    gap:2px;padding:72px 18px 24px;overflow-y:auto;
    background:var(--pg-surface,#fff);color:var(--pg-text);
    box-shadow:-8px 0 32px rgba(0,0,0,.18);
    transform:${from};transition:transform .28s cubic-bezier(.4,0,.2,1);
  }
  .${cls} .pg-nav-toggle:checked ~ .pg-nav-list{transform:none}
  .${cls} .pg-nav-toggle:checked ~ .pg-nav-scrim{display:block;position:fixed;inset:0;z-index:999;background:rgba(15,23,42,.45)}
  /* In the drawer, submenus are always expanded — a hover-driven flyout is
     unusable on touch, and nesting a tap-to-open state would need JS the
     static site can't rely on. */
  .${cls} .pg-nav-list .pg-navmenu-dropdown{
    position:static!important;display:flex!important;flex-direction:column!important;
    border:none!important;box-shadow:none!important;background:transparent!important;
    padding:0 0 0 14px!important;min-width:0!important;
  }
  .${cls} .pg-nav-list .pg-navmenu-dropdown.pg-mega{display:flex!important;grid-template-columns:none}
  .${cls} .pg-nav-list a,.${cls} .pg-nav-list .pg-navmenu-trigger{width:100%;padding:12px 10px;font-size:1rem}
  .${cls} .pg-nav-list .pg-navmenu-item{width:100%}
}
@media (prefers-reduced-motion: reduce){
  .${cls} .pg-nav-list{transition:none}
}`
}

// ════════════════════════════════════════════════════════════════════
// Widget CSS: lightbox, before/after, consent, announcement, carousel,
// pricing toggle, bento grid, social, steps, comparison table
// ════════════════════════════════════════════════════════════════════
export const WIDGET_CSS = `
/* ── Gallery lightbox ── */
.pg-lb{position:fixed;inset:0;z-index:1200;display:none;align-items:center;justify-content:center;
  background:rgba(8,10,16,.92);padding:24px}
.pg-lb[data-open]{display:flex}
.pg-lb-fig{margin:0;max-width:92vw;max-height:88vh;display:flex;flex-direction:column;gap:10px;align-items:center}
.pg-lb-fig img{max-width:92vw;max-height:80vh;object-fit:contain;border-radius:8px;display:block}
.pg-lb-fig figcaption{color:#e5e7eb;font-size:.85rem;text-align:center}
.pg-lb button{position:absolute;background:rgba(255,255,255,.12);color:#fff;border:none;cursor:pointer;
  width:44px;height:44px;border-radius:50%;font-size:24px;line-height:1;display:flex;align-items:center;justify-content:center}
.pg-lb-x{top:20px;right:20px}
.pg-lb-p{left:20px;top:50%;transform:translateY(-50%)}
.pg-lb-n{right:20px;top:50%;transform:translateY(-50%)}
.pg-lb button:hover{background:rgba(255,255,255,.22)}
/* Masonry gallery — CSS columns keep each image's own proportions. */
.pg-gallery-masonry{column-gap:var(--pg-gal-gap,12px)}
.pg-gallery-masonry > figure{break-inside:avoid;margin:0 0 var(--pg-gal-gap,12px)}

/* ── Before / after ── */
.pg-ba{position:relative;overflow:hidden;border-radius:10px;--pg-ba:50%}
.pg-ba img{display:block;width:100%;height:100%;object-fit:cover}
.pg-ba-after{position:absolute;inset:0;clip-path:inset(0 0 0 var(--pg-ba))}
.pg-ba[data-orient="vertical"] .pg-ba-after{clip-path:inset(var(--pg-ba) 0 0 0)}
.pg-ba-handle{position:absolute;top:0;bottom:0;left:var(--pg-ba);width:2px;background:#fff;
  box-shadow:0 0 0 1px rgba(0,0,0,.25);pointer-events:none}
.pg-ba[data-orient="vertical"] .pg-ba-handle{left:0;right:0;top:var(--pg-ba);bottom:auto;width:auto;height:2px}
.pg-ba-knob{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  width:38px;height:38px;border-radius:50%;background:#fff;box-shadow:0 2px 10px rgba(0,0,0,.3);
  display:flex;align-items:center;justify-content:center;font-size:14px;color:#111}
.pg-ba-range{position:absolute;inset:auto 0 12px;width:calc(100% - 24px);margin:0 12px;z-index:3;cursor:ew-resize}
.pg-ba-label{position:absolute;top:12px;padding:4px 10px;border-radius:999px;font-size:.72rem;font-weight:700;
  background:rgba(0,0,0,.55);color:#fff;z-index:2}
.pg-ba-label-b{left:12px}
.pg-ba-label-a{right:12px}

/* ── Cookie consent ── */
.pg-consent{position:fixed;z-index:1100;display:none;gap:14px;align-items:center;flex-wrap:wrap;
  padding:16px 18px;background:var(--pg-elevated,#fff);color:var(--pg-text);
  border:1px solid var(--pg-border);border-radius:12px;box-shadow:0 16px 48px rgba(0,0,0,.22);
  max-width:min(640px,calc(100vw - 32px))}
.pg-consent[data-open]{display:flex}
.pg-consent-bottom{left:50%;transform:translateX(-50%);bottom:20px}
.pg-consent-top{left:50%;transform:translateX(-50%);top:20px}
.pg-consent-bottom-left{left:20px;bottom:20px}
.pg-consent-bottom-right{right:20px;bottom:20px}
.pg-consent-msg{flex:1;min-width:220px;font-size:.86rem;line-height:1.5}
.pg-consent-actions{display:flex;gap:8px;flex-wrap:wrap}
.pg-consent-cats{display:none;width:100%;flex-direction:column;gap:8px;padding-top:10px;
  border-top:1px solid var(--pg-border)}
.pg-consent[data-prefs] .pg-consent-cats{display:flex}
.pg-consent[data-prefs] [data-pg-prefs]{display:none}
.pg-consent-cat{display:flex;gap:10px;align-items:flex-start;font-size:.82rem}

/* ── Announcement bar ── */
.pg-announce{display:flex;align-items:center;justify-content:center;gap:12px;
  padding:9px 44px 9px 16px;font-size:.85rem;position:relative;text-align:center;flex-wrap:wrap}
.pg-announce a{color:inherit;text-decoration:underline;font-weight:600}
.pg-announce [data-pg-dismiss]{position:absolute;right:10px;top:50%;transform:translateY(-50%);
  background:none;border:none;color:inherit;cursor:pointer;opacity:.7;font-size:18px;line-height:1;padding:4px}
.pg-announce [data-pg-dismiss]:hover{opacity:1}

/* ── Generic carousel (block + testimonial slider) ── */
.pg-car{position:relative;overflow:hidden;border-radius:12px}
.pg-car-track{position:relative}
.pg-car-slide{position:absolute;inset:0;opacity:0;transition:opacity .6s ease;pointer-events:none}
.pg-car-slide.is-active{opacity:1;pointer-events:auto;position:relative}
.pg-car-dots{position:absolute;left:0;right:0;bottom:12px;display:flex;justify-content:center;gap:8px;z-index:3}
.pg-car-dot{width:9px;height:9px;border-radius:999px;border:none;padding:0;cursor:pointer;
  background:color-mix(in srgb,currentColor 35%,transparent)}
.pg-car-dot.is-active{background:currentColor}
.pg-car-arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:3;width:38px;height:38px;
  border-radius:999px;border:none;cursor:pointer;background:rgba(0,0,0,.32);color:#fff;font-size:22px;
  display:flex;align-items:center;justify-content:center}
.pg-car-arrow[data-arrow="prev"]{left:12px}
.pg-car-arrow[data-arrow="next"]{right:12px}
@media (prefers-reduced-motion: reduce){.pg-car-slide{transition:none}}

/* ── Pricing period toggle ── */
.pg-pricing .pg-price-alt{display:none}
.pg-pricing[data-alt] .pg-price-alt{display:inline}
.pg-pricing[data-alt] .pg-price-main{display:none}
.pg-price-switch{display:inline-flex;align-items:center;gap:10px;align-self:center;
  padding:5px;border-radius:999px;border:1px solid var(--pg-border);background:var(--pg-surface)}
.pg-price-switch label{cursor:pointer;padding:6px 14px;border-radius:999px;font-size:.82rem;font-weight:600}
.pg-price-track{position:relative;width:44px;height:24px;border-radius:999px;background:var(--pg-border);cursor:pointer}
.pg-price-track::after{content:'';position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;
  background:var(--pg-surface);transition:transform .2s ease;box-shadow:0 1px 3px rgba(0,0,0,.25)}
.pg-price-toggle:checked ~ .pg-price-track{background:var(--pg-primary)}
.pg-price-toggle:checked ~ .pg-price-track::after{transform:translateX(20px)}
.pg-price-toggle{position:absolute;opacity:0;width:1px;height:1px}

/* ── Bento grid ── */
.pg-bento{display:grid;grid-template-columns:repeat(var(--pg-bento-cols,4),minmax(0,1fr));
  gap:var(--pg-bento-gap,16px);grid-auto-rows:minmax(180px,auto)}
.pg-bento-cell{display:flex;flex-direction:column;gap:8px;padding:22px;border-radius:16px;
  background:var(--pg-surface);border:1px solid var(--pg-border);overflow:hidden;position:relative}
@media (max-width:1024px){.pg-bento{grid-template-columns:repeat(2,minmax(0,1fr))}
  .pg-bento-cell{grid-column:span 1!important}
  .pg-bento-cell[data-wide]{grid-column:span 2!important}}
@media (max-width:640px){.pg-bento{grid-template-columns:1fr}
  .pg-bento-cell,.pg-bento-cell[data-wide]{grid-column:span 1!important;grid-row:span 1!important}}

/* ── Steps / process ── */
.pg-steps{display:grid;gap:20px}
.pg-steps-horizontal{grid-template-columns:repeat(auto-fit,minmax(200px,1fr))}
.pg-step{display:flex;gap:12px;align-items:flex-start}
.pg-step-num{flex:0 0 auto;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;
  justify-content:center;font-weight:800;background:var(--pg-primary);color:var(--pg-on-primary,#fff)}
.pg-steps-horizontal .pg-step{flex-direction:column}
/* The connector line between steps — the thing that makes it read as a
   process rather than a list. Not drawn after the last step. */
.pg-steps-horizontal .pg-step:not(:last-child) .pg-step-num::after{
  content:'';position:absolute;left:34px;right:-100%;top:17px;height:2px;
  background:linear-gradient(90deg,var(--pg-border),transparent)}
.pg-steps-horizontal .pg-step-num{position:relative}

/* ── Comparison table ── */
.pg-cmp-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch}
.pg-cmp{border-collapse:collapse;width:100%;min-width:520px}
.pg-cmp th,.pg-cmp td{padding:12px 14px;text-align:center;border-bottom:1px solid var(--pg-border)}
.pg-cmp th:first-child,.pg-cmp td:first-child{text-align:left;font-weight:600}
.pg-cmp thead th{background:var(--pg-surface);font-weight:700}
.pg-cmp-sticky thead th{position:sticky;top:0;z-index:2}
.pg-cmp-hi{background:var(--pg-surface-alt)}
.pg-cmp-group td{background:var(--pg-surface-muted);font-weight:700;text-align:left;font-size:.78rem;
  text-transform:uppercase;letter-spacing:.06em}
.pg-cmp-yes{color:var(--pg-success)}
.pg-cmp-no{color:var(--pg-subtle)}

/* ── Social links ── */
.pg-social{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.pg-social a{display:inline-flex;align-items:center;justify-content:center;color:inherit;
  text-decoration:none;transition:transform .15s ease,color .15s ease}
.pg-social a:hover{color:var(--pg-primary)}
.pg-social-circle a,.pg-social-square a{width:38px;height:38px;border:1px solid var(--pg-border);
  background:var(--pg-surface)}
.pg-social-circle a{border-radius:50%}
.pg-social-square a{border-radius:10px}
@media (prefers-reduced-motion: no-preference){.pg-social a:hover{transform:translateY(-2px)}}

/* ── Team ── */
.pg-team-photo{width:100%;object-fit:cover;display:block;background:var(--pg-surface-alt)}
.pg-team-circle .pg-team-photo{border-radius:50%;aspect-ratio:1/1}
.pg-team-rounded .pg-team-photo{border-radius:14px;aspect-ratio:4/5}
.pg-team-square .pg-team-photo{border-radius:0;aspect-ratio:1/1}

/* ── Eyebrow token ── */
.pg-eyebrow{font-size:var(--pg-eyebrow-size,12px);font-weight:var(--pg-eyebrow-weight,700);
  letter-spacing:var(--pg-eyebrow-tracking,.08em);text-transform:var(--pg-eyebrow-transform,uppercase);
  color:var(--pg-accent);display:block}
`

/**
 * The one CSS bundle every surface injects. Keeping the concatenation here
 * (rather than in each surface) is what stops the four surfaces from drifting
 * over which effects they support — adding a global stylesheet to this
 * string lands it on the canvas, the preview, the runtime and the static
 * site at once.
 */
// The filter bar's chosen facet. `initFilterBars` flips `aria-pressed` on the
// chips, but nothing styled it, so picking a category changed the grid and
// left every chip looking the same. `!important` because the chips paint
// their resting look inline.
const FILTER_CHIP_CSS = `
[data-pg-facet-pick]{transition:background-color .15s ease,border-color .15s ease,color .15s ease}
[data-pg-facet-pick]:hover{border-color:var(--pg-primary)!important}
[data-pg-facet-pick][aria-pressed="true"]{background:var(--pg-primary)!important;border-color:var(--pg-primary)!important;color:var(--pg-on-primary,#fff)!important;font-weight:600}
[data-pg-facet-pick][aria-pressed="true"] [data-pg-count-for]{background:rgba(255,255,255,.22)!important;color:inherit!important}
[data-pg-filter-layout="sidebar"] [data-pg-facet-pick]{width:100%;justify-content:space-between;border-radius:10px!important;text-align:left}
@media (max-width:1024px){
  [data-pg-filter-layout="sidebar"] .pg-facet-list{flex-direction:row!important;flex-wrap:nowrap!important;overflow-x:auto;padding-bottom:4px;scrollbar-width:thin}
  [data-pg-filter-layout="sidebar"] [data-pg-facet-pick]{width:auto;flex:0 0 auto;border-radius:999px!important;white-space:nowrap}
}
`

export function pageRuntimeCss(): string {
  return [
    DEVICE_VISIBILITY_CSS,
    FILTER_CHIP_CSS,
    ANIMATION_CSS,
    ANIMATED_BG_CSS,
    SECTION_LAYOUT_CSS,
    TOOLTIP_CSS,
    MARQUEE_CSS,
    PAGE_CHROME_CSS,
    NAV_CSS,
    WIDGET_CSS,
    LAZY_SECTION_CSS,
  ].join('\n')
}

// The vanilla scripts live in their own module (they are pure strings, not
// theme data) but are re-exported here so every surface keeps ONE import
// site for "the shared page-runtime CSS + behaviour".
export * from './scripts'
// The cart's DOM wiring ships the same way the other page scripts do.
export { cartScript } from './cart'
// …and so does the wishlist's, its sibling.
export { wishlistScript } from './wishlist'

// ════════════════════════════════════════════════════════════════════
// Color math helpers — used to derive missing palette tokens from
// explicit ones. Kept simple (HSL + sRGB-luminance) to avoid pulling in
// a color lib for what is ultimately a small number of derivations.
// ════════════════════════════════════════════════════════════════════

// Parse a CSS-ish color string to RGB. Handles `#rgb`, `#rrggbb`,
// `rgb(...)`, `rgba(...)`. Returns null on anything else (named colors,
// hsl(), CSS variables) — derivations then fall back to the explicit
// preset defaults instead of trying to invent a color from a `var(...)`.
function parseColor(c: string): { r: number; g: number; b: number; a: number } | null {
  if (!c) return null
  const s = c.trim()
  // #rgb / #rrggbb
  if (s.startsWith('#')) {
    const hex = s.slice(1)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16)
      const g = parseInt(hex[1] + hex[1], 16)
      const b = parseInt(hex[2] + hex[2], 16)
      if ([r, g, b].every(n => Number.isFinite(n))) return { r, g, b, a: 1 }
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      if ([r, g, b].every(n => Number.isFinite(n))) return { r, g, b, a: 1 }
    }
    return null
  }
  // rgb(...) / rgba(...)
  const m = s.match(/^rgba?\(([^)]+)\)$/i)
  if (m) {
    const parts = m[1].split(',').map(p => parseFloat(p.trim()))
    if (parts.length >= 3 && parts.every(p => Number.isFinite(p))) {
      return { r: parts[0], g: parts[1], b: parts[2], a: parts[3] ?? 1 }
    }
  }
  return null
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
  const h = (n: number) => clamp(n).toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`
}

// Perceptual luminance (sRGB). 0 = black, 1 = white. Used to decide
// whether a color is "dark" enough to warrant white text on top.
function luminance(c: string): number {
  const rgb = parseColor(c)
  if (!rgb) return 1 // unknown → assume light, prefer dark text fallback
  const channel = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

// Is this color dark enough that body text should be near-white?
// Threshold tuned so deep slates/blues qualify but warm creams don't.
export function isColorDark(c: string): boolean {
  return luminance(c) < 0.4
}

// WCAG contrast ratio between two colors. Returns 1..21. 4.5+ passes
// AA for body text, 3+ passes AA for large text or non-essential UI.
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

// Return `explicit` only if it's parseable AND it contrasts `against`
// at the requested ratio. Otherwise return `fallback`. Used by
// `derivePalette` to "rescue" saved palettes whose persisted colors no
// longer pair with the current background (e.g. text=white inherited
// from a dark-theme save, after the user swapped to a light bg). We
// prefer the user's explicit value when it's readable; only fall back
// when it would silently render invisible text.
function ensureContrast(
  explicit: string | undefined,
  against: string,
  minRatio: number,
  fallback: string,
): string {
  if (!explicit) return fallback
  // If we can't parse `against` (e.g. a CSS var leaks through), trust
  // the explicit value — we have no basis for an override.
  if (parseColor(against) == null) return explicit
  // If the explicit value isn't a parseable color, fall back rather
  // than render an unknown color we can't validate.
  if (parseColor(explicit) == null) return fallback
  return contrastRatio(explicit, against) >= minRatio ? explicit : fallback
}

// Pick a contrast-safe foreground for a color. Used for `onPrimary`,
// `onAccent`, badge text-on-pill, etc. Returns near-white on dark
// brand colors, near-black on light brand colors.
export function pickOn(c: string): string {
  return isColorDark(c) ? '#ffffff' : '#0f172a'
}

// Mix a color toward black (negative amt) or white (positive amt).
// `amt` is a 0..1 fraction; e.g. 0.10 = 10% toward white.
function shade(c: string, amt: number): string {
  const rgb = parseColor(c)
  if (!rgb) return c
  if (amt >= 0) {
    return toHex(rgb.r + (255 - rgb.r) * amt, rgb.g + (255 - rgb.g) * amt, rgb.b + (255 - rgb.b) * amt)
  }
  const a = -amt
  return toHex(rgb.r * (1 - a), rgb.g * (1 - a), rgb.b * (1 - a))
}

const lighten = (c: string, amt: number) => shade(c, amt)
const darken = (c: string, amt: number) => shade(c, -amt)

// Wrap a color in an rgba() with the given alpha. If we can't parse
// it (e.g. it's a CSS var or named color), fall back to the original.
function withAlpha(c: string, a: number): string {
  const rgb = parseColor(c)
  if (!rgb) return c
  return `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${a})`
}
